/**
 * TriEye Product Configurator — SHARED LIBRARY
 *
 * This file provides shared functions used by all product snippets
 * (the-view-variants-2026, the-classic-variants-2026, the-clip-variants-2026).
 *
 * IMPORTANT: This file must NOT register any event handlers (click, submit,
 * DOMContentLoaded, etc). Each snippet owns its own handlers to avoid
 * double-fire bugs. This file is eval'd via synchronous XHR before snippet
 * init code runs.
 *
 * Gallery filtering uses image alt text to match variants:
 *   Alt text format: "FrameColor_LensType" (e.g. "Matte Black_Polarized Smoke")
 *   - Exact match → show
 *   - Fallback: same lens type, different frame → show (only if no exact match)
 *   - "#color-handle" format (e.g. "#matte-charcoal") → show for that color
 *   - No underscore or empty alt → shared image, always show
 *   - No match → hide
 */

/* ═══════════════════════════════════════════
   STATE INITIALIZATION (fallback only)
   ═══════════════════════════════════════════ */

if (typeof state === 'undefined') {
  window.state = { lens: null, color: null, size: null, mirror: null, group: null };
}
if (typeof selectedAddons === 'undefined') { window.selectedAddons = []; }

/* ═══════════════════════════════════════════
   MIRROR STRIP HELPERS (2026-05-14)
   Pure-logic helpers used by snippets/view-mirror-strip.liquid.
   NO event handlers in this file (per top-of-file rule). The snippet
   owns its own DOM wiring.
   ═══════════════════════════════════════════ */

window.TrieyeMirrorStrip = window.TrieyeMirrorStrip || (function () {
  var KEY = 'trieye:view:lastConfig';
  var MAX_AGE_MS = 14 * 24 * 60 * 60 * 1000;

  function decideMirrorDefault(opts) {
    // opts: { url, referrer }
    var url = (opts && opts.url) || (typeof location !== 'undefined' ? location.href : '');
    var referrer = (opts && opts.referrer) || (typeof document !== 'undefined' ? document.referrer : '');
    var u, params;
    try { u = new URL(url); params = u.searchParams; }
    catch (e) { return { mirrorType: 'single' }; }
    var activity = (params.get('activity') || '').toLowerCase();
    var utmCampaign = (params.get('utm_campaign') || '').toLowerCase();
    var utmTerm = (params.get('utm_term') || '').toLowerCase();
    var ref = (referrer || '').toLowerCase();
    var rowingSignal =
      activity === 'rowing' ||
      utmCampaign.indexOf('rowing') !== -1 ||
      utmTerm.indexOf('rowing') !== -1 ||
      ref.indexOf('/rowing') !== -1 ||
      ref.indexOf('/collections/rowing') !== -1;
    return { mirrorType: rowingSignal ? 'dual' : 'single' };
  }

  function persistMirrorState(state) {
    try {
      var payload = {};
      for (var k in state) if (Object.prototype.hasOwnProperty.call(state, k)) payload[k] = state[k];
      payload.timestamp = Date.now();
      localStorage.setItem(KEY, JSON.stringify(payload));
    } catch (e) { /* localStorage may be disabled */ }
  }

  function restoreMirrorState() {
    try {
      var raw = localStorage.getItem(KEY);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      if (!obj.timestamp || Date.now() - obj.timestamp > MAX_AGE_MS) return null;
      return obj;
    } catch (e) { return null; }
  }

  // 2026-05-14: hide color/lens swatches whose (lens, color, size) combo has
  // no variant available for the current state.mirror. Pure helper — caller
  // (view-mirror-strip.liquid <script>) decides when to invoke (on mirror
  // change + on swatch rebuild). No event handlers here per CLAUDE.md.
  //
  // 2026-05-14 (round 2): extended to handle ALL mirror states (Left/Right/
  // Dual), not just Dual. Previously Single Left/Right swatches got no
  // filtering — e.g. "Standard Clear" swatch showed when state.mirror was
  // Left Mirror, even though no Standard Clear / Left variant exists.
  function filterColorSwatchesByMirror() {
    if (typeof window.variants === 'undefined' && typeof variants === 'undefined') return;
    var V = (typeof window.variants !== 'undefined') ? window.variants : variants;
    if (!V || !V.length) return;
    var mirror = '';
    if (typeof state !== 'undefined' && state.mirror) mirror = state.mirror;
    if (!mirror) return;
    var mt = mirror.toLowerCase();
    var mirrorTag = mt.indexOf('dual') >= 0 ? 'Dual'
                  : mt.indexOf('right') >= 0 ? 'Right'
                  : mt.indexOf('left') >= 0 ? 'Left'
                  : '';
    if (!mirrorTag) return;
    // Build set of (lens|color|size) combos that should appear in the swatch
    // picker for the current mirror. Rules:
    //   1. variant matches current mirror tag
    //   2. variant is available, OR variant has the variant metafield
    //      trieye.show_when_sold_out = true (editorial override — keeps the
    //      swatch visible with a sold-out label, see swatch builder)
    // window.__trieyeShowWhenSoldOut is a map { variantId: bool } emitted by
    // the View Liquid; defaults to false for every variant unless flipped
    // in Shopify admin.
    var showOOSMap = window.__trieyeShowWhenSoldOut || {};
    var validCombos = Object.create(null);
    var anyValid = false;
    for (var i = 0; i < V.length; i++) {
      var v = V[i];
      var opt3 = (v.option3 || '');
      if (opt3.indexOf(mirrorTag) === -1) continue;
      var allow = v.available || !!showOOSMap[v.id];
      if (!allow) continue;
      var sizeParts = opt3.split('—');
      var size = (sizeParts[sizeParts.length - 1] || '').trim();
      validCombos[(v.option1 || '') + '|' + (v.option2 || '') + '|' + size] = true;
      anyValid = true;
    }
    var swatches = document.querySelectorAll('.px-color-medium .px-btn, .px-color-small .px-btn');
    // Safety net: if no valid combos at all, don't filter. Could mean
    // the catalog truly has none for this mirror (rare) OR something
    // upstream went wrong (window.variants missing, mirror state corrupt).
    // Hiding every swatch is worse than showing everything; the user
    // can still see what's possible.
    if (!anyValid) {
      for (var k = 0; k < swatches.length; k++) {
        swatches[k].classList.remove('is-mirror-incompatible');
      }
      return;
    }
    for (var j = 0; j < swatches.length; j++) {
      var btn = swatches[j];
      var lens = btn.getAttribute('data-lens') || '';
      var color = btn.getAttribute('data-color') || '';
      var size = btn.getAttribute('data-size') || '';
      var combo = lens + '|' + color + '|' + size;
      var incompatible = !validCombos[combo];
      if (incompatible) btn.classList.add('is-mirror-incompatible');
      else btn.classList.remove('is-mirror-incompatible');
    }
  }

  // 2026-05-14: hide lens-group buttons (.px-group) whose group has NO
  // available variant for the current state.mirror. Same CSS class
  // (.is-mirror-incompatible) — CSS rule update adds .px-group to the hide
  // selector. If the active group becomes hidden, auto-switch to the first
  // visible group via programmatic click (triggers the existing group click
  // chain: sets state.group, runs dual-correct, runs filterAndAutoSelect).
  function filterLensGroupsByMirror() {
    if (typeof window.variants === 'undefined') return;
    var V = window.variants;
    if (!V || !V.length) return;
    var mirror = (typeof state !== 'undefined' && state.mirror) ? state.mirror : '';
    if (!mirror) return;
    var mt = mirror.toLowerCase();
    var mirrorTag = mt.indexOf('dual') >= 0 ? 'Dual'
                  : mt.indexOf('right') >= 0 ? 'Right'
                  : mt.indexOf('left') >= 0 ? 'Left' : '';
    if (!mirrorTag) return;
    var showOOSMap = window.__trieyeShowWhenSoldOut || {};
    var groupIds = window.__groupVariantIds || {};
    var groupBtns = document.querySelectorAll('.px-groups .px-group');
    var anyVisible = false;
    var firstVisibleKey = null;
    for (var b = 0; b < groupBtns.length; b++) {
      var btn = groupBtns[b];
      var gKey = btn.getAttribute('data-group-key') || btn.getAttribute('data-group') || '';
      var ids = groupIds[gKey] || [];
      var hasMatch = false;
      for (var i = 0; i < V.length; i++) {
        var v = V[i];
        if (ids.length && ids.indexOf(v.id) === -1) continue;
        if ((v.option3 || '').indexOf(mirrorTag) === -1) continue;
        if (!(v.available || !!showOOSMap[v.id])) continue;
        hasMatch = true;
        break;
      }
      if (hasMatch) {
        btn.classList.remove('is-mirror-incompatible');
        anyVisible = true;
        if (!firstVisibleKey) firstVisibleKey = gKey;
      } else {
        btn.classList.add('is-mirror-incompatible');
      }
    }
    if (!anyVisible) {
      for (var k = 0; k < groupBtns.length; k++) groupBtns[k].classList.remove('is-mirror-incompatible');
      return;
    }
    var activeBtn = document.querySelector('.px-groups .px-group.active');
    if (activeBtn && activeBtn.classList.contains('is-mirror-incompatible') && firstVisibleKey) {
      var newBtn = document.querySelector('.px-groups .px-group[data-group-key="' + firstVisibleKey + '"]')
                || document.querySelector('.px-groups .px-group[data-group="' + firstVisibleKey + '"]');
      if (newBtn && newBtn !== activeBtn) newBtn.click();
    }
  }

  // 2026-05-14: find a Dual-compatible variant in the same group as the user's
  // current state.lens. Used to auto-correct state.lens when switching to Dual
  // from a lens that has no Dual variant (e.g. "Reflective Red" → "Reflective
  // Blue"). Caller patches state.lens / state.color BEFORE clicking the hidden
  // Dual button so filterAndAutoSelect picks the right variant.
  function findDualCompatibleVariant(currentState) {
    if (!currentState) return null;
    var V = (typeof window.variants !== 'undefined') ? window.variants : (typeof variants !== 'undefined' ? variants : null);
    if (!V || !V.length) return null;
    var groupIds = (window.__groupVariantIds || {})[currentState.group] || null;
    // 2026-07-28 (v90): prefer Sharp-generation variants (The View V2) over
    // Original when both have a Dual option in the group — mirrors the
    // Sharp-first default in filterAndAutoSelect. Pass 1 considers only
    // Sharp variants, pass 2 everything; each pass keeps the existing
    // keep-current-color preference. No-op on products without Sharp
    // variants (Classic, Clip).
    var passes = [
      function (v) { return /sharp/i.test(v.option1 || ''); },
      function () { return true; }
    ];
    var inGroupFallback = null;
    for (var p = 0; p < passes.length; p++) {
      for (var i = 0; i < V.length; i++) {
        var v = V[i];
        if (!passes[p](v)) continue;
        if ((v.option3 || '').indexOf('Dual') === -1) continue;
        if (!v.available) continue;
        var inGroup = groupIds ? (groupIds.indexOf(v.id) !== -1) : true;
        if (!inGroup) continue;
        // Prefer a variant that keeps current color (less disruption).
        if (currentState.color && v.option2 === currentState.color) {
          return v;
        }
        // Otherwise stash the first available Dual variant in the group as
        // fallback.
        if (!inGroupFallback) inGroupFallback = v;
      }
      if (inGroupFallback) return inGroupFallback;
    }

    // 2026-05-15: cross-group fallback. When the current lens group has
    // ZERO Dual variants at all (e.g. High Contrast on The View), the
    // in-group search above returns null. Previously this caused the
    // Dual click to silently no-op — state stayed on the
    // Dual-incompatible lens, mirror reverted to Single. User
    // experience: clicking Dual did nothing.
    //
    // Now: iterate `.px-groups .px-group` in DOM order (so the leftmost
    // lens type that supports Dual is preferred — Standard first, then
    // HC, then Reflective, etc.). For each group, look up its variant
    // IDs via window.__groupVariantIds and return the first available
    // Dual variant. forwardToExisting() will pick this up and re-sync
    // state.group + the active .px-group button + state.lens + state.color
    // (and state.size) before forwarding the click.
    var groupBtns = (typeof document !== 'undefined') ? document.querySelectorAll('.px-groups .px-group') : [];
    var allGroupIds = window.__groupVariantIds || {};
    for (var g = 0; g < groupBtns.length; g++) {
      var gKey = groupBtns[g].getAttribute('data-group-key') || groupBtns[g].getAttribute('data-group') || '';
      var ids = allGroupIds[gKey] || [];
      for (var k = 0; k < ids.length; k++) {
        var cand = V.find(function (x) { return x.id === ids[k]; });
        if (cand && (cand.option3 || '').indexOf('Dual') !== -1 && cand.available) {
          return cand;
        }
      }
    }
    return null;
  }

  return {
    decideMirrorDefault: decideMirrorDefault,
    persistMirrorState: persistMirrorState,
    restoreMirrorState: restoreMirrorState,
    filterColorSwatchesByMirror: filterColorSwatchesByMirror,
    filterLensGroupsByMirror: filterLensGroupsByMirror,
    findDualCompatibleVariant: findDualCompatibleVariant
  };
})();

/* ═══════════════════════════════════════════
   LIGHTBOX (self-contained, zero dependencies)
   ═══════════════════════════════════════════ */

var _trieyelb = null; // singleton lightbox instance

/**
 * Open a fullscreen lightbox from our custom gallery grid.
 * If galleryEl.__lightboxItems is present (new 3-section gallery), use that.
 * Otherwise fall back to collecting .trieye-gallery__slide (legacy/mobile).
 */
function openTrieyeLightbox(galleryEl, startIndex) {
  var items;

  // Prefer a pre-built items array if the caller cached one on the gallery node.
  // The desktop 3-section layout (main + thumbs + lifestyle) uses this so the
  // lightbox can navigate across all images regardless of which section was clicked.
  if (galleryEl && galleryEl.__lightboxItems && galleryEl.__lightboxItems.length) {
    items = galleryEl.__lightboxItems;
  } else {
    var slides = galleryEl.querySelectorAll('.trieye-gallery__slide:not(.trieye-gallery__slide--placeholder)');
    if (!slides.length) return;
    items = [];
    slides.forEach(function(slide) {
      if (slide.classList.contains('trieye-gallery__slide--video')) {
        var videoSrc = slide.getAttribute('data-video-src') || '';
        var poster = slide.querySelector('img');
        items.push({
          type: 'video',
          videoSrc: videoSrc,
          poster: poster ? (poster.currentSrc || poster.src) : ''
        });
      } else {
        var img = slide.querySelector('img');
        if (img) {
          items.push({
            type: 'image',
            src: _lbBestSrc(img),
            thumb: img.currentSrc || img.src
          });
        }
      }
    });
  }

  if (!items.length) return;
  if (startIndex >= items.length) startIndex = items.length - 1;
  if (startIndex < 0) startIndex = 0;

  // Create or reuse the overlay
  if (!_trieyelb) _trieyelb = _lbCreate();
  _trieyelb.open(items, startIndex);
}

/** Extract largest URL from srcset */
function _lbBestSrc(img) {
  var srcset = img.getAttribute('srcset') || '';
  if (srcset) {
    var best = null, bestW = 0;
    srcset.split(',').forEach(function(entry) {
      var p = entry.trim().split(/\s+/);
      var w = parseInt(p[1], 10) || 0;
      if (w > bestW) { best = p[0]; bestW = w; }
    });
    if (best) return best;
  }
  var src = img.getAttribute('src') || img.src || '';
  return src.replace(/_(\d+x\d+|\d+x)(\.[a-z]+)/i, '_1600x$2');
}

/** Build the lightbox DOM (once) */
function _lbCreate() {
  // Overlay container
  var overlay = document.createElement('div');
  overlay.className = 'trieye-lightbox';
  overlay.innerHTML =
    '<div class="trieye-lightbox__backdrop"></div>' +
    '<div class="trieye-lightbox__content">' +
      '<div class="trieye-lightbox__media"></div>' +
    '</div>' +
    '<button class="trieye-lightbox__close" aria-label="Close">&times;</button>' +
    '<button class="trieye-lightbox__prev" aria-label="Previous">' +
      '<svg viewBox="0 0 24 24" width="28" height="28"><polyline points="15,18 9,12 15,6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
    '</button>' +
    '<button class="trieye-lightbox__next" aria-label="Next">' +
      '<svg viewBox="0 0 24 24" width="28" height="28"><polyline points="9,6 15,12 9,18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
    '</button>' +
    '<div class="trieye-lightbox__counter"></div>';

  document.body.appendChild(overlay);

  var mediaEl = overlay.querySelector('.trieye-lightbox__media');
  var counterEl = overlay.querySelector('.trieye-lightbox__counter');
  var closeBtn = overlay.querySelector('.trieye-lightbox__close');
  var prevBtn = overlay.querySelector('.trieye-lightbox__prev');
  var nextBtn = overlay.querySelector('.trieye-lightbox__next');

  var items = [];
  var current = 0;

  // ── Native-feeling zoom (iOS Photos style) ──
  // Two design choices that make this feel native vs. "web-app":
  //
  //   1. Pinch zoom is anchored to the FINGER MIDPOINT, not the image
  //      center. The image-space point under the user's pinch midpoint
  //      stays under their fingers as they zoom. Combined with finger-
  //      tracking translate, this matches the "rubber under glass"
  //      feel of the iOS Photos app.
  //
  //   2. Transform uses transform-origin: 0 0 with translate(tx,ty) +
  //      scale(s). With origin at top-left, the matrix math is clean:
  //          screen_pos = image_local_pos * scale + (tx, ty)
  //      Inverting:
  //          image_local_pos = (screen_pos - (tx, ty)) / scale
  //      So when we want a known image_local_pos to be at a known
  //      target screen_pos after a scale change:
  //          tx = target_x - image_local_pos.x * scale
  //          ty = target_y - image_local_pos.y * scale
  //      That's the formula used in onPinchMove and onDoubleTap below.
  //
  // Other native-feel details: zero transition during active gestures,
  // smooth ease-out on release, double-tap zooms toward the tap point
  // (not center), and zoom resets between slides.
  var scale = 1, savedScale = 1;
  var tx = 0, ty = 0;
  var pinch = null;     // { startDist, startMid, startScale, imgX, imgY }
  var panOrigin = null; // { x, y } — finger pos minus current translate
  var lastTapTime = 0, lastTapX = 0, lastTapY = 0;
  var GESTURE_EASE = 'transform 0.28s cubic-bezier(0.22, 0.84, 0.36, 1)';

  function _activeImg() { return mediaEl.querySelector('.trieye-lightbox__img'); }
  function _midpoint(a, b) {
    return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 };
  }
  function _applyTransform(animate) {
    var img = _activeImg();
    if (!img) return;
    img.style.transformOrigin = '0 0';
    img.style.transition = animate ? GESTURE_EASE : 'none';
    img.style.transform = 'translate(' + tx + 'px,' + ty + 'px) scale(' + scale + ')';
  }
  function _resetZoomState() {
    scale = 1; savedScale = 1;
    tx = 0; ty = 0;
    pinch = null; panOrigin = null;
    var img = _activeImg();
    if (img) {
      img.style.transition = GESTURE_EASE;
      img.style.transform = '';
    }
  }

  function show(idx) {
    current = idx;
    _resetZoomState();
    var item = items[idx];
    // Clean up previous
    mediaEl.innerHTML = '';

    if (item.type === 'video' && item.videoSrc) {
      var iframe = document.createElement('iframe');
      iframe.src = item.videoSrc + (item.videoSrc.indexOf('?') >= 0 ? '&' : '?') + 'autoplay=1';
      iframe.setAttribute('allow', 'autoplay; fullscreen');
      iframe.setAttribute('allowfullscreen', '');
      iframe.className = 'trieye-lightbox__video';
      mediaEl.appendChild(iframe);
    } else if (item.type === 'video' && item.poster) {
      var img = document.createElement('img');
      img.src = item.poster;
      img.className = 'trieye-lightbox__img';
      mediaEl.appendChild(img);
    } else {
      var img2 = document.createElement('img');
      img2.src = item.src;
      img2.className = 'trieye-lightbox__img';
      // Show thumb instantly, swap to full-res when loaded
      if (item.thumb && item.thumb !== item.src) {
        img2.src = item.thumb;
        var full = new Image();
        full.onload = function() { img2.src = item.src; };
        full.src = item.src;
      }
      mediaEl.appendChild(img2);
    }

    counterEl.textContent = (idx + 1) + ' / ' + items.length;
    prevBtn.style.display = items.length > 1 ? '' : 'none';
    nextBtn.style.display = items.length > 1 ? '' : 'none';
  }

  function close() {
    overlay.classList.remove('is-open');
    mediaEl.innerHTML = '';
    document.body.style.overflow = '';
    document.removeEventListener('keydown', onKey);
  }

  function prev() { show((current - 1 + items.length) % items.length); }
  function next() { show((current + 1) % items.length); }

  function onKey(e) {
    if (e.key === 'Escape') close();
    else if (e.key === 'ArrowLeft') prev();
    else if (e.key === 'ArrowRight') next();
  }

  closeBtn.addEventListener('click', close);
  prevBtn.addEventListener('click', function(e) { e.stopPropagation(); prev(); });
  nextBtn.addEventListener('click', function(e) { e.stopPropagation(); next(); });
  overlay.querySelector('.trieye-lightbox__backdrop').addEventListener('click', close);

  // Swipe-to-navigate is gated on scale===1 so it doesn't fire while the
  // user is panning a zoomed image. Lives on overlay (not mediaEl) so a
  // swipe that starts on the backdrop also navigates.
  var touchStartX = 0, touchStartY = 0;
  overlay.addEventListener('touchstart', function(e) {
    if (scale > 1) return;
    if (e.changedTouches.length !== 1) return;
    touchStartX = e.changedTouches[0].clientX;
    touchStartY = e.changedTouches[0].clientY;
  }, { passive: true });
  overlay.addEventListener('touchend', function(e) {
    if (scale > 1) return;
    if (e.changedTouches.length !== 1) return;
    var dx = e.changedTouches[0].clientX - touchStartX;
    var dy = e.changedTouches[0].clientY - touchStartY;
    // Only horizontal swipes count as navigation; ignore vertical/diagonal so
    // a casual scroll-down gesture doesn't accidentally page the lightbox.
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) {
      dx > 0 ? prev() : next();
    }
  }, { passive: true });

  // ── Pinch / pan / double-tap on the image ──
  // Listener strategy:
  //   touchstart  passive: true   — just records initial state
  //   touchmove   passive: false  — needs preventDefault to suppress iOS
  //                                 Safari's native pinch/page-zoom and to
  //                                 stop the overlay swipe handler from
  //                                 mistaking a pan as a navigate
  //   touchend    passive: true   — bookkeeping only
  mediaEl.addEventListener('touchstart', function(e) {
    if (!_activeImg()) return;
    if (e.touches.length === 2) {
      // Save EVERYTHING needed to compute finger-anchored zoom in touchmove.
      // Critically, store the image-space point that's currently under the
      // pinch midpoint — that point should stay put under the user's
      // fingers throughout the gesture.
      var mid = _midpoint(e.touches[0], e.touches[1]);
      pinch = {
        startDist: Math.hypot(
          e.touches[0].clientX - e.touches[1].clientX,
          e.touches[0].clientY - e.touches[1].clientY
        ),
        startScale: scale,
        startTx: tx,
        startTy: ty,
        // image_local_point = (screen_pos - translate) / scale
        imgX: (mid.x - tx) / scale,
        imgY: (mid.y - ty) / scale
      };
    } else if (e.touches.length === 1 && scale > 1) {
      // Start a pan: save the offset between finger position and current
      // translate so panMove just subtracts.
      panOrigin = {
        x: e.touches[0].clientX - tx,
        y: e.touches[0].clientY - ty
      };
    }
  }, { passive: true });

  mediaEl.addEventListener('touchmove', function(e) {
    if (!_activeImg()) return;
    if (pinch && e.touches.length === 2) {
      e.preventDefault();
      var mid = _midpoint(e.touches[0], e.touches[1]);
      var dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      // Allow zoom-out below 1 down to 0.6 so the "pinch to dismiss / shrink"
      // gesture has some give; clamped back to >=1 on touchend (or reset).
      scale = Math.min(4, Math.max(0.6, pinch.startScale * (dist / pinch.startDist)));
      // Keep pinch.imgX / pinch.imgY stationary under the current finger
      // midpoint:  screen = imgPoint * scale + translate  →  translate = screen - imgPoint * scale
      tx = mid.x - pinch.imgX * scale;
      ty = mid.y - pinch.imgY * scale;
      _applyTransform(false);
    } else if (panOrigin && e.touches.length === 1 && scale > 1) {
      e.preventDefault();
      tx = e.touches[0].clientX - panOrigin.x;
      ty = e.touches[0].clientY - panOrigin.y;
      _applyTransform(false);
    }
  }, { passive: false });

  mediaEl.addEventListener('touchend', function(e) {
    if (!_activeImg()) return;

    // End pinch
    if (pinch && e.touches.length < 2) {
      pinch = null;
      // Snap back to 1× if user pinched out below threshold (matches iOS
      // Photos behavior: small pinch-down doesn't keep the image shrunk).
      if (scale < 1.05) {
        _resetZoomState();
      } else {
        savedScale = scale;
        _applyTransform(true);  // gentle ease at end of gesture
      }
    }

    // End pan
    if (panOrigin && e.touches.length === 0) {
      panOrigin = null;
    }

    // Double-tap toggle — anchor the new zoom toward the tap location, not
    // the image center, so users can zoom into the part of the image they
    // tapped (matches iOS Photos behavior).
    if (e.touches.length === 0 && e.changedTouches.length === 1 && !pinch) {
      var now = Date.now();
      var tap = e.changedTouches[0];
      var nearLastTap = Math.abs(tap.clientX - lastTapX) < 40 && Math.abs(tap.clientY - lastTapY) < 40;
      if (now - lastTapTime < 300 && lastTapTime > 0 && nearLastTap) {
        if (scale > 1) {
          _resetZoomState();
        } else {
          // Tap point in image-local coords (before zoom):
          var imgX = (tap.clientX - tx) / scale;
          var imgY = (tap.clientY - ty) / scale;
          scale = 2.5;
          // Anchor: imageLocal point should be at the tap screen position after zoom.
          tx = tap.clientX - imgX * scale;
          ty = tap.clientY - imgY * scale;
          savedScale = scale;
          _applyTransform(true);
        }
        lastTapTime = 0;
      } else {
        lastTapTime = now;
        lastTapX = tap.clientX;
        lastTapY = tap.clientY;
      }
    }
  }, { passive: true });

  return {
    open: function(newItems, idx) {
      items = newItems;
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKey);
      show(idx || 0);
    }
  };
}

/* ═══════════════════════════════════════════
   GALLERY FILTERING
   ═══════════════════════════════════════════ */

/**
 * Normalize translated lens names to match English image alt texts.
 */
function normalizeLensName(name) {
  if (!name) return name;
  return name.replace(/Høy\s*Kontrast/i, 'High Contrast');
}

/*
 * Gallery rebuild system.
 *
 * On page load, we snapshot ALL slide and thumbnail HTML into _galleryCache.
 * On variant change, we empty the slideshow, insert only matching slides,
 * and trigger a resize so the theme recreates Flickity with the right cells.
 * No display:none on Flickity cells — ever.
 */
var _galleryCache = null;   // { slides: [{html, filter, mediaId}], thumbs: [{html, filter, mediaId}] }
var _lastGalleryFilter = '';

/**
 * Reset a scroll-snap container to position 0 reliably on iOS Safari.
 *
 * iOS re-evaluates snap position across several layout passes after an
 * innerHTML swap — once on first paint, and again after images finish
 * loading (loading="eager" triggers reflow). A single rAF isn't always
 * enough; a remembered snap index can re-apply on a later pass.
 *
 * Strategy: disable snap, force scrollLeft = 0, run through two rAF
 * frames, then re-check at 150ms (covers image-load reflow) and
 * correct once more if iOS has drifted the scroll back.
 */
function _resetTrackScroll(track) {
  if (!track) return;
  track.style.scrollSnapType = 'none';
  track.scrollLeft = 0;
  requestAnimationFrame(function() {
    track.scrollLeft = 0;
    requestAnimationFrame(function() {
      track.scrollLeft = 0;
      track.style.scrollSnapType = '';
    });
  });
  // Safety net: verify after layout + image-load settle. If iOS has
  // re-applied a remembered snap index, forcibly pull it back to 0.
  setTimeout(function() {
    if (track.scrollLeft === 0) return;
    track.style.scrollSnapType = 'none';
    track.scrollLeft = 0;
    requestAnimationFrame(function() {
      track.style.scrollSnapType = '';
    });
  }, 150);
}

/**
 * Place the Classic PDP "NEW" badge inside the visible image container.
 *
 * The badge is rendered as a sibling of `.trieye-gallery` in Liquid
 * (sections/product.liquid). When it sat on the outer wrapper its top:12/
 * left:12 offsets landed in the wrapper's padding zone — i.e. *outside*
 * the actual image. Moving it into the visible image container makes
 * those same offsets land at the upper-left corner of the image itself.
 *
 *   Desktop: target = .trieye-gallery__main (the hero image cell — has
 *            position:relative, aspect-ratio 4/3, border-radius 10px).
 *   Mobile : target = .trieye-gallery       (the carousel container —
 *            position:relative, contains the scroll-snap track + dots).
 *
 * The desktop gallery is rebuilt via `gallery.innerHTML = ...` on every
 * variant change, which detaches the badge but doesn't destroy the node.
 * We cache the reference so we can re-append after each rebuild.
 */
function _placePdpNewBadge(gallery, isMobile) {
  var badge = _placePdpNewBadge._cached;
  if (!badge) {
    badge = document.querySelector('.trieye-pdp__new-badge');
    if (!badge) return;
    _placePdpNewBadge._cached = badge;
  }
  var target = isMobile ? gallery : gallery.querySelector('.trieye-gallery__main');
  if (target && badge.parentNode !== target) {
    target.appendChild(badge);
  }
}

/** Cache all slides + thumbnails from the DOM (called once on init) */
function _cacheGallery() {
  if (_galleryCache) return;
  _galleryCache = { slides: [], thumbs: [] };

  document.querySelectorAll('[data-media-slide]').forEach(function(slide) {
    _galleryCache.slides.push({
      html: slide.outerHTML,
      filter: (slide.getAttribute('data-variant-filter') || '').trim(),
      mediaId: slide.getAttribute('data-media-id')
    });
  });

  document.querySelectorAll('[data-slideshow-thumbnail]').forEach(function(thumb) {
    _galleryCache.thumbs.push({
      html: thumb.outerHTML,
      filter: (thumb.getAttribute('data-variant-filter') || '').trim(),
      mediaId: thumb.getAttribute('data-media-select')
    });
  });
}

/**
 * filterGalleryByColor(color, lens)
 * Rebuilds the gallery with only matching slides.
 */
function filterGalleryByColor(color, lens) {
  // 1. Resolve lens type
  var lensType = normalizeLensName(lens || '');
  if (!lensType && typeof state !== 'undefined' && state.lens) {
    lensType = normalizeLensName(state.lens);
  }
  if (!lensType) {
    var checked = document.querySelector('[name="options[Lens Type]"]:checked');
    if (checked) lensType = checked.value;
  }

  var colorLower = (color || '').toLowerCase().trim();
  if (!colorLower) return;

  // 2026-05-07: Variant option2 reads "White" but image alts are tagged
  // "Matte White_*" (per the {FrameColor}_{Lens} convention where black is
  // "Matte Black"). Without aliasing, the gallery never matches White
  // photochromic slides — they stay hidden and the previously-active
  // Matte Black slides remain on screen. Map the variant option to the
  // alt-tag form *for filter matching only*. state.color stays "White"
  // everywhere else (model header, variant lookup, cart).
  // Only `colorLower` and downstream keys (filterKey, frameColorKey) are
  // remapped; `colorHandle` keeps the original so any `#white` slides
  // (currently none, but reserved) still resolve correctly.
  // 2026-09-23 (REGRESJON, andre gang): aliaset `white -> matte white` er
  // FJERNET IGJEN. Det ble fjernet 2026-08-27 og hadde krøpet tilbake.
  // Platene det pekte pa finnes ikke: `Matte White_*` er utelukkende
  // Sharp-fargeveien (Reflective Silver Sharp), en annen fysisk ramme.
  // Med aliaset traff filteret ingenting og galleriet ble staaende paa
  // Matte Black for hvit Original. Hvit skal mappe rett gjennom.
  // Alt-tagger som skal treffes: `White_Photochromic Smoke 0-2_Front`,
  // `White_Photochromic Smoke 0-3_Front`.
  var _colorAlt = colorLower;

  // Detect Dual mirror frame — reads state.mirror (e.g. "Left Mirror", "Right Mirror", "Dual Mirror")
  var mirrorSig = '';
  if (typeof state !== 'undefined' && state.mirror) {
    mirrorSig = state.mirror.toLowerCase();
  }
  var isDual = mirrorSig.indexOf('dual') >= 0;

  // Dedup — keyed on the FULL mirror string, not a dual/single boolean.
  // 2026-08-27 (bugfix): the signature used to collapse the mirror to
  // `dual` vs `single`, so "Left Mirror", "Right Mirror" and a not-yet-set
  // mirror all produced the SAME signature. Consequences: (a) switching
  // Left <-> Right never re-filtered the gallery, and (b) on a ?variant=
  // deep-link the first call runs before state.mirror is seeded, renders
  // every side at once, and then permanently blocks the correct re-filter.
  // Both failures put right-mirror thumbnails in front of left-mirror
  // buyers — the exact wrong-side confusion the side gating prevents.
  var filterSig = lensType + '|' + colorLower + '|' + (mirrorSig || 'unset');
  if (_lastGalleryFilter === filterSig) return;
  _lastGalleryFilter = filterSig;

  // 2. Build filter keys — format: {FrameColor}_{LensType}[_Dual]
  var filterKey = lensType ? (_colorAlt + '_' + lensType).toLowerCase().trim() : '';
  if (isDual && filterKey) {
    filterKey = filterKey + '_dual';                       // e.g. "matte black_reflective blue_dual"
  }
  var colorHandle = '#' + colorLower.replace(/\s+/g, '-');
  var frameColorKey = _colorAlt;                           // e.g. "matte black" (or alias)
  var lensTypeKey = lensType ? lensType.toLowerCase().trim() : '';  // e.g. "high contrast smoke"
  if (isDual && lensTypeKey) {
    lensTypeKey = lensTypeKey + '_dual';                   // lens-level fallback also requires dual
  }

  // 3. Cache gallery on first run
  _cacheGallery();
  if (!_galleryCache || !_galleryCache.slides.length) return;

  // 4. Check if any cached slides have an exact-combo match for this variant.
  //    Accepts filterKey itself OR any filterKey + technical-shot suffix
  //    (_Front, _Angle, _Side, _Left, _Right) so the presence of suffixed
  //    images still counts as an "exact match" and doesn't trigger the
  //    cross-frame fallback.
  //    View uses: _Front, _Angle, _Side. Classic uses: _Left, _Right.
  var hasExactMatch = false;
  if (filterKey) {
    var _techSuffixes = ['_front', '_angle', '_side', '_back', '_left', '_right'];
    hasExactMatch = _galleryCache.slides.some(function(s) {
      if (s.filter === filterKey) return true;
      for (var i = 0; i < _techSuffixes.length; i++) {
        if (s.filter === filterKey + _techSuffixes[i]) return true;
      }
      return false;
    });
  }

  // ── Dual-variant tech-shot inheritance ──
  // When the user is viewing a Dual variant but dedicated dual tech shots for
  // this lens/color don't exist (e.g. HCS Dual has lifestyle shots only),
  // fall back to the single-mirror tech shot of the MATCHING category.
  // Rationale: the glasses' front/angle/side profile is near-identical between
  // single and dual configurations — only the inner mirror housing differs.
  // Showing a single-mirror angle shot on a dual variant is better than
  // showing no product shot at all.
  //
  // Fallback is category-by-category: if dual has its own _Angle we use that;
  // we only pull in single's _Angle when dual doesn't have one. Prevents
  // duplicate thumbs on variants like Reflective Blue Dual that do have full
  // native dual tech coverage.
  var singleFilterKey = null;
  var _dualHasFront = false, _dualHasAngle = false, _dualHasSide = false, _dualHasBack = false;
  if (isDual && filterKey) {
    singleFilterKey = filterKey.replace(/_dual$/, '');
    _galleryCache.slides.forEach(function(s) {
      if (s.filter === filterKey + '_front') _dualHasFront = true;
      if (s.filter === filterKey + '_angle') _dualHasAngle = true;
      if (s.filter === filterKey + '_side')  _dualHasSide  = true;
      if (s.filter === filterKey + '_back')  _dualHasBack  = true;
    });
  }

  // ── Sharp (V2) lifestyle/in-box inheritance ── (2026-07-16)
  // Sharp lenses currently ship with only _Front/_Angle studio shots, so a
  // Sharp build rendered a 2-image gallery with no lifestyle or in-the-box
  // context. Same rationale as the dual→single fallback above: the lens is
  // identical to the Original's (lenses interchange across View frames) and
  // lifestyle/in-box shots read the same at that distance, so inherit those
  // categories from the matching Original {color}_{lens} key. Sharp's own
  // _Front/_Angle hero shots are NOT overridden — V1/V2 frame close-ups
  // must stay accurate. Category-by-category: native Sharp media wins the
  // moment it's uploaded, making this fallback self-retiring.
  var originalFilterKey = null;
  var _sharpHasLifestyle = false, _sharpHasBox = false, _sharpHasSide = false, _sharpHasBack = false;
  if (filterKey && lensTypeKey && / sharp(_dual)?$/.test(lensTypeKey)) {
    originalFilterKey = filterKey.replace(' sharp', '');
    _galleryCache.slides.forEach(function(s) {
      if (s.filter === filterKey) _sharpHasLifestyle = true;
      if (s.filter === filterKey + '_in-the-box') _sharpHasBox = true;
      if (s.filter === filterKey + '_side') _sharpHasSide = true;
      if (s.filter === filterKey + '_back') _sharpHasBack = true;
    });
  }

  // 5. Match function
  //    Priority: exact combo match > suffixed tech shot > single-mirror tech fallback
  //              > cross-frame lens fallback > shared
  function isMatch(filter) {
    if (!filter) return 'match';                           // no alt text = shared, always show
    if (filter.startsWith('[ph]')) return 'hide';          // placeholder images — never show
    if (filter.startsWith('#')) {
      return (filter === colorHandle) ? 'match' : 'hide'; // #color-handle format
    }
    if (!filter.includes('_')) return 'match';             // no underscore = shared, always show
    if (filterKey) {
      if (filter === filterKey) return 'match';            // exact {frame}_{lens}[_dual] match
      // Accept technical-shot alt suffixes on the same filterKey:
      //   View:    _Front / _Angle / _Side
      //   Classic: _Left / _Right (two profile views)
      if (filter === filterKey + '_front') return 'match';
      if (filter === filterKey + '_angle') return 'match';
      if (filter === filterKey + '_side')  return 'match';
      if (filter === filterKey + '_back')  return 'match';
      if (filter === filterKey + '_left')  return 'match';
      if (filter === filterKey + '_right') return 'match';
      if (filter === filterKey + '_in-the-box') return 'match';
      // Dual → Single inheritance per shot category (only when dual lacks this view)
      if (isDual && singleFilterKey) {
        if (!_dualHasFront && filter === singleFilterKey + '_front') return 'match';
        if (!_dualHasAngle && filter === singleFilterKey + '_angle') return 'match';
        if (!_dualHasSide  && filter === singleFilterKey + '_side')  return 'match';
        if (!_dualHasBack  && filter === singleFilterKey + '_back')  return 'match';
      }
      // Sharp → Original inheritance for lifestyle/in-box/side/back
      // (only for categories the Sharp build lacks natively — see block above)
      if (originalFilterKey) {
        if (!_sharpHasLifestyle && filter === originalFilterKey) return 'match';
        if (!_sharpHasBox  && filter === originalFilterKey + '_in-the-box') return 'match';
        if (!_sharpHasSide && filter === originalFilterKey + '_side') return 'match';
        if (!_sharpHasBack && filter === originalFilterKey + '_back') return 'match';
      }
      // Fallback: if no exact match, show images with same lens type (different frame)
      if (!hasExactMatch && lensTypeKey) {
        var parts = filter.split('_');
        if (parts.length > 1 && parts.slice(1).join('_') === lensTypeKey) return 'match';
      }
      return 'hide';
    }
    // No filterKey (no lens type set, e.g. Clip product) — match by color prefix
    if (frameColorKey && filter.split('_')[0] === frameColorKey) {
      // Side-specific images: alt carries a side token (_left / _right / _dual)
      // anywhere after the color prefix — e.g. the Original Clip's trailing
      // "..._left" OR the Clip Sharp convention "<color> sharp_clip_<side>_<role>"
      // ("polarized smoke sharp_clip_left_front"). Only show on the variant whose
      // mirror side matches. Non-side alts (no token) always show.
      // 2026-08-13: added `dual` + allowed the token to precede a trailing role
      // suffix, so Clip Sharp Left/Right/Dual galleries stay side-correct.
      var sideSuffix = filter.match(/_(left|right|dual)(?=_|$)/);
      if (sideSuffix) {
        var variantMirror = (typeof state !== 'undefined' && state.mirror) ? state.mirror.toLowerCase() : '';
        var variantSide = variantMirror.indexOf('dual') >= 0 ? 'dual'
                        : variantMirror.indexOf('left') >= 0 ? 'left'
                        : variantMirror.indexOf('right') >= 0 ? 'right'
                        : '';
        if (variantSide && sideSuffix[1] !== variantSide) return 'hide';
      }
      return 'match';
    }
    return 'hide';
  }

  // 6. Get matching slides and thumbs from cache (preserves original order)
  var matchingSlides = _galleryCache.slides.filter(function(s) { return isMatch(s.filter) !== 'hide'; });
  var matchingThumbs = _galleryCache.thumbs.filter(function(t) { return isMatch(t.filter) !== 'hide'; });

  // Find first matching media ID for navigation
  var firstMatchMediaId = matchingSlides.length > 0 ? matchingSlides[0].mediaId : null;

  // 2026-05-12 (Dual mobile fix): pre-sort matchingSlides by alt-suffix rank
  // so the MOBILE carousel order (which uses DOM order directly) puts
  // Front → Angle → Side → Back → in-the-box → lifestyle, regardless of
  // Shopify position. Previously a Dual variant whose _Dual_Side sat at
  // a lower Shopify position than _Dual_Front ended up with Dual_Side as
  // the mobile hero. Desktop's classifier re-sorts within buckets so this
  // pre-sort is a no-op there.
  function _topLevelAltSuffixRank(s) {
    var altMatch = s.html && s.html.match(/alt="([^"]*)"/);
    var alt = (altMatch ? altMatch[1] : '').toLowerCase();
    if (/_lifestyle($|_)/.test(alt)) return 7;   // see note in _classify
    if (/_front($|_)/.test(alt)) return 0;
    if (/_angle($|_)/.test(alt)) return 1;
    if (/_side($|_)/.test(alt))  return 2;
    if (/_back($|_)/.test(alt))  return 3;
    if (/_in-the-box($|_)/.test(alt)) return 4;
    if (/_right($|_)/.test(alt)) return 5;
    if (/_left($|_)/.test(alt))  return 6;
    return 7;
  }
  // Stable sort: items with same rank keep relative DOM order.
  matchingSlides = matchingSlides.map(function(s, i) { return { s: s, i: i, r: _topLevelAltSuffixRank(s) }; })
    .sort(function(a, b) { return (a.r - b.r) || (a.i - b.i); })
    .map(function(x) { return x.s; });
  matchingThumbs = matchingThumbs.map(function(t, i) { return { s: t, i: i, r: _topLevelAltSuffixRank(t) }; })
    .sort(function(a, b) { return (a.r - b.r) || (a.i - b.i); })
    .map(function(x) { return x.s; });

  // 7. Desktop vs Mobile gallery approach
  var mediaWrapper = document.querySelector('.product__media__wrapper');
  if (!mediaWrapper) return;
  var themeSlideshow = mediaWrapper.querySelector('[data-product-slideshow]');
  var isMobile = window.innerWidth < 768;

  // Hide theme's native slideshow and thumbs (both paths)
  if (themeSlideshow) themeSlideshow.style.display = 'none';
  var thumbHolder = mediaWrapper.querySelector('[data-thumbs-holder]');
  if (thumbHolder) thumbHolder.style.display = 'none';

  // Create or reuse our custom gallery container
  var gallery = mediaWrapper.querySelector('.trieye-gallery');
  if (!gallery) {
    gallery = document.createElement('div');
    gallery.className = 'trieye-gallery';
    mediaWrapper.insertBefore(gallery, themeSlideshow);
  }
  gallery.style.display = '';

  // DESKTOP: 3-section layout
  //   1. Main hero (single large image, starts as front technical shot)
  //   2. Thumbnail row (small clickable thumbs, swap the main)
  //   3. Lifestyle grid (2-col grid of model/context shots, click → lightbox)
  //
  // Classification is done on each matchingSlide by looking at alt text suffix
  // (_Angle / _Side → tech-secondary) and filename keywords (front, anglefront,
  // side, left → tech-primary). Everything else is lifestyle.
  if (!isMobile) {
    // Sort videos to end (they end up in lifestyle)
    matchingSlides.sort(function(a, b) {
      var aV = a.html.indexOf('data-type="video"') >= 0 || a.html.indexOf('data-type="external_video"') >= 0;
      var bV = b.html.indexOf('data-type="video"') >= 0 || b.html.indexOf('data-type="external_video"') >= 0;
      return aV - bV;
    });

    // ── Classify each slide ──
    //
    // Three categories with STRICT, deterministic rules:
    //
    //   tech-primary  — clean "front" product shot. Alt ends with "_Front" OR
    //                   filename ends with "front.png" (e.g. polarizedfront.png,
    //                   photofront3.png). Used as the default main hero.
    //
    //   tech-secondary — angle/side/profile product shot. Alt ends with "_Angle"
    //                    or "_Side", OR filename ends with "anglefront.png" /
    //                    "side.png" / "left.png".
    //
    //   lifestyle — everything else (model shots, rowing/cycling context, video).
    //
    // Thumbnail row only ever contains tech-primary + tech-secondary.
    // Lifestyle shots NEVER appear in the thumbnail row, even if they end up
    // as the main hero via the fallback chain.
    function _extractSrc(html) {
      var m = html && html.match(/src="([^"]+)"/);
      return m ? m[1] : '';
    }
    function _extractAlt(html) {
      var m = html && html.match(/alt="([^"]*)"/);
      return m ? m[1] : '';
    }
    // Classifier returns both the category AND the method used ("alt" or
    // "filename"). Method matters for the thumb row: filename-matched
    // fallbacks (old product shots with `-left` in filename but no alt
    // suffix) can still feed the main-hero fallback, but they MUST NOT
    // appear in the thumb row — the filenames are sometimes mislabeled
    // (e.g. `classic-mattbrown-with-highcontrast-left.png` is tagged as
    // Matte Umber_Polarized Brown), so showing them confuses customers.
    function _classify(slide) {
      if (slide.mediaId && slide.mediaId.indexOf('placeholder') === 0) return { kind: 'placeholder', method: null };
      if (slide.html.indexOf('data-type="video"') >= 0 || slide.html.indexOf('data-type="external_video"') >= 0) return { kind: 'lifestyle', method: null };
      var alt = (_extractAlt(slide.html) || slide.filter || '').toLowerCase();

      // 1. Explicit alt-text suffix (preferred, unambiguous)
      //    All products use _Front, _Angle, _Back (Classic also has legacy _Left/_Right).
      //    Both _Angle and _Front are tech-primary — both are valid main-hero
      //    candidates. The hero-pick later (via _altSuffixRank) prefers _Angle
      //    because the 3/4 view shows TriEye's signature mirror feature best:
      //    Classic's side clip-on housing pokes out, View's in-lens mirror
      //    coating catches light at the angle.
    // 2026-08-27: side-gated LIFESTYLE images. The gallery gates a photo to a
    // mirror side via a _left/_right/_dual token in the alt, but _left/_right
    // ALSO mean "technical product shot" to the classifier below. A lifestyle
    // photo of the left-mirror Clip therefore needs both: the side token to be
    // gated correctly, and an explicit _lifestyle marker (checked FIRST) so it
    // is not merchandised as a studio shot in the thumb row.
    if (/_lifestyle($|_)/.test(alt)) return { kind: 'lifestyle', method: 'alt' };
      if (/_angle($|_)/.test(alt)) return { kind: 'tech-primary',   method: 'alt' };
      if (/_front($|_)/.test(alt)) return { kind: 'tech-primary',   method: 'alt' };
      if (/_side($|_)/.test(alt))  return { kind: 'tech-secondary', method: 'alt' };
      if (/_back($|_)/.test(alt))  return { kind: 'tech-secondary', method: 'alt' };
      if (/_left($|_)/.test(alt))  return { kind: 'tech-secondary', method: 'alt' };
      if (/_right($|_)/.test(alt)) return { kind: 'tech-secondary', method: 'alt' };
      // "What's in the box" flat-lay — appears in thumb row alongside tech shots
      if (/_in-the-box($|_)/.test(alt)) return { kind: 'tech-secondary', method: 'alt' };

      // 2. Filename pattern — fallback for legacy images without alt suffix.
      //    Shopify appends a UUID suffix; strip extension + uuid before matching.
      var src = _extractSrc(slide.html).toLowerCase().split('/').pop().split('?')[0];
      var stem = src.replace(/\.(png|jpe?g|webp|gif)$/, '');
      stem = stem.replace(/_[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/, '');

      if (/anglefront$/.test(stem))                     return { kind: 'tech-secondary', method: 'filename' };
      if (/(^|[-_])(side|left|right)$/.test(stem))      return { kind: 'tech-secondary', method: 'filename' };
      if (/front\d*(white\d*)?$/.test(stem))            return { kind: 'tech-primary',   method: 'filename' };

      return { kind: 'lifestyle', method: null };
    }

    var techPrimary = [];         // for main hero (any method)
    var techSecondary = [];       // for main hero (any method)
    var techPrimaryAlt = [];      // for thumb row (explicit alt only)
    var techSecondaryAlt = [];    // for thumb row (explicit alt only)
    var lifestyleSlides = [];
    matchingSlides.forEach(function(s) {
      var c = _classify(s);
      if (c.kind === 'placeholder') return;
      if (c.kind === 'tech-primary') {
        techPrimary.push(s);
        if (c.method === 'alt') techPrimaryAlt.push(s);
      } else if (c.kind === 'tech-secondary') {
        techSecondary.push(s);
        if (c.method === 'alt') techSecondaryAlt.push(s);
      } else {
        lifestyleSlides.push(s);
      }
    });

    // Rank tech images to pick the best "main hero" candidate.
    //
    // Preferred order (lower = earlier = more likely to become main):
    //   _Front → _Angle → _Side → _Back → _Right → _Left → filename-only
    //
    // Rationale: _Front is the canonical hero shot. For Classic, _Front is
    // the front-with-mirror image (clip-on visible head-on) — already shows
    // the iconic TriEye feature. For View, _Front shows the in-lens mirror
    // coating. _Angle ranks second so it's the first thumbnail customers
    // click. Legacy _Right/_Left (interim two-view scheme) rank lower;
    // filename-only last.
    function _altSuffixRank(s) {
      var alt = (_extractAlt(s.html) || '').toLowerCase();
      if (/_front($|_)/.test(alt)) return 0;
      if (/_lifestyle($|_)/.test(alt)) return 7;   // see note in _classify
      if (/_angle($|_)/.test(alt)) return 1;
      if (/_side($|_)/.test(alt))  return 2;
      if (/_back($|_)/.test(alt))  return 3;
      // _in-the-box ranks 4 so it lands as the 4th thumb (after Front/Angle/Side
      // for variants without _Back/_Right/_Left). Below tech rotation views,
      // above the legacy Right/Left and filename-matched fallbacks.
      if (/_in-the-box($|_)/.test(alt)) return 4;
      if (/_right($|_)/.test(alt)) return 5;
      if (/_left($|_)/.test(alt))  return 6;
      return 7; // no suffix (filename-matched)
    }
    // All four lists sort by the same rank so _Angle always comes first
    // (becomes main hero), _Front second (first thumb), _Back third, etc.
    techPrimary.sort(function(a, b)       { return _altSuffixRank(a) - _altSuffixRank(b); });
    techPrimaryAlt.sort(function(a, b)    { return _altSuffixRank(a) - _altSuffixRank(b); });
    techSecondary.sort(function(a, b)     { return _altSuffixRank(a) - _altSuffixRank(b); });
    techSecondaryAlt.sort(function(a, b)  { return _altSuffixRank(a) - _altSuffixRank(b); });

    // ── Main hero selection (graceful fallback chain) ──
    //   1st choice: first tech-primary (front shot) — ideal
    //   2nd choice: first tech-secondary (angle shot) — still a product shot
    //   3rd choice: first lifestyle — last resort, no product shot exists yet
    //
    // Note: the fallback is for the MAIN hero only. The thumbnail row still
    // ONLY contains real tech shots (primary + secondary) — it never includes
    // a lifestyle shot even if that shot was promoted to main.
    var mainSlide = techPrimary[0] || techSecondary[0] || lifestyleSlides[0];
    if (!mainSlide) {
      gallery.innerHTML = '';
      return;
    }
    var mainIsLifestyle = !techPrimary.length && !techSecondary.length;
    // If main is a lifestyle shot, don't remove it from the lifestyle list —
    // we want it to still appear in the grid below. Most variants won't hit this
    // branch (they have at least a front shot).

    // Helper to extract <img> attributes as an object for building our own markup
    function _parseImg(html) {
      var tmp = document.createElement('div');
      tmp.innerHTML = html;
      var img = tmp.querySelector('img[srcset], img[src]');
      if (!img) return null;
      return {
        src: img.getAttribute('src') || '',
        srcset: img.getAttribute('srcset') || '',
        alt: img.getAttribute('alt') || '',
        w: img.getAttribute('width') || '800',
        h: img.getAttribute('height') || '800'
      };
    }

    // ── Build main hero HTML from mainSlide (chosen via fallback chain above) ──
    var mainImg = _parseImg(mainSlide.html);
    var mainSlideHTML = '';
    if (mainImg) {
      // 2026-05-12 (perf): add decoding="async" so image decode doesn't
      // block the main thread. eager + high fetchpriority for LCP.
      mainSlideHTML =
        '<img src="' + mainImg.src + '"' +
        ' srcset="' + mainImg.srcset + '"' +
        ' sizes="(min-width: 1100px) 560px, 50vw"' +
        ' width="' + mainImg.w + '" height="' + mainImg.h + '"' +
        ' alt="' + mainImg.alt + '"' +
        ' loading="eager" fetchpriority="high" decoding="async" draggable="false">';
    }

    // ── Build thumbnail row HTML — STRICTLY explicit-alt tech shots.
    //    Using techPrimaryAlt + techSecondaryAlt (not the broader
    //    techPrimary/techSecondary) excludes filename-matched fallbacks,
    //    because legacy filenames are sometimes mislabeled (a file named
    //    "classic-mattbrown-with-highcontrast-left.png" was tagged as
    //    Matte Umber_Polarized Brown). Keeping the row alt-only means
    //    customers only see images that were explicitly reviewed + tagged.
    //    Never include lifestyle shots here, even if main is lifestyle. ──
    var thumbSlides = [];
    var _seenSrc = {};
    techPrimaryAlt.concat(techSecondaryAlt).forEach(function(s) {
      var pi = _parseImg(s.html);
      if (!pi) return;
      if (_seenSrc[pi.src]) return;
      _seenSrc[pi.src] = true;
      thumbSlides.push(pi);
    });
    // Hide the row entirely if we don't have at least 2 real product shots —
    // no point showing a 1-item row.
    var thumbsHTML = '';
    if (thumbSlides.length >= 2 && mainImg) {
      thumbSlides.forEach(function(t) {
        // Active thumb = whichever matches the current main image src
        var isActive = t.src === mainImg.src;
        thumbsHTML +=
          '<button type="button" class="trieye-gallery__thumb' + (isActive ? ' is-active' : '') + '"' +
          ' data-src="' + t.src + '"' +
          ' data-srcset="' + t.srcset + '"' +
          ' data-alt="' + t.alt + '"' +
          ' aria-label="View ' + (t.alt || 'image') + '">' +
          '<img src="' + t.src + '"' +
          ' srcset="' + t.srcset + '"' +
          ' sizes="80px"' +
          ' width="144" height="144"' +
          ' alt="' + t.alt + '"' +
          ' loading="lazy" decoding="async" draggable="false">' +
          '</button>';
      });
    }

    // ── Build lifestyle grid HTML ──
    var lifestyleGridHTML = '';
    lifestyleSlides.forEach(function(s) {
      if (s.html.indexOf('data-type="video"') >= 0 || s.html.indexOf('data-type="external_video"') >= 0) {
        var tmpv = document.createElement('div');
        tmpv.innerHTML = s.html;
        var posterImg = tmpv.querySelector('img');
        var videoEl = tmpv.querySelector('iframe, video');
        var videoSrc = videoEl ? (videoEl.getAttribute('src') || videoEl.getAttribute('data-src') || '') : '';
        lifestyleGridHTML += '<div class="trieye-gallery__slide trieye-gallery__slide--video"' +
          (videoSrc ? ' data-video-src="' + videoSrc.replace(/"/g, '&quot;') + '"' : '') + '>' +
          (posterImg ? '<img src="' + posterImg.src + '" alt="">' : '<div style="aspect-ratio:1/1;background:#f0f0f0"></div>') +
          '<div class="trieye-play-overlay"><svg viewBox="0 0 24 24"><polygon points="5,3 19,12 5,21"/></svg></div>' +
          '</div>';
        return;
      }
      var li = _parseImg(s.html);
      if (!li) return;
      // 2026-05-12 (perf): decoding="async" — image decode happens off
      // the main thread, freeing it for layout/JS work. Pairs with lazy.
      lifestyleGridHTML += '<div class="trieye-gallery__slide">' +
        '<img src="' + li.src + '"' +
        ' srcset="' + li.srcset + '"' +
        ' sizes="(min-width: 1100px) 280px, 50vw"' +
        ' width="' + li.w + '" height="' + li.h + '"' +
        ' alt="' + li.alt + '"' +
        ' loading="lazy" decoding="async" draggable="false">' +
        '</div>';
    });

    // ── Build unified lightbox items array ──
    // Order: all tech shots (from thumbs) + all lifestyle shots (inc. videos).
    // Stored on gallery.__lightboxItems so openTrieyeLightbox uses it directly
    // instead of DOM-scanning .trieye-gallery__slide.
    var lightboxItems = [];
    thumbSlides.forEach(function(t) {
      lightboxItems.push({
        type: 'image',
        // _lbBestSrc equivalent inline — pick largest from srcset
        src: (function(){
          if (t.srcset) {
            var best = null, bestW = 0;
            t.srcset.split(',').forEach(function(entry) {
              var p = entry.trim().split(/\s+/);
              var w = parseInt(p[1], 10) || 0;
              if (w > bestW) { best = p[0]; bestW = w; }
            });
            if (best) return best;
          }
          return t.src.replace(/_(\d+x\d+|\d+x)(\.[a-z]+)/i, '_1600x$2');
        })(),
        thumb: t.src
      });
    });
    lifestyleSlides.forEach(function(s) {
      if (s.html.indexOf('data-type="video"') >= 0 || s.html.indexOf('data-type="external_video"') >= 0) {
        var tmpv = document.createElement('div');
        tmpv.innerHTML = s.html;
        var posterImg = tmpv.querySelector('img');
        var videoEl = tmpv.querySelector('iframe, video');
        var videoSrc = videoEl ? (videoEl.getAttribute('src') || videoEl.getAttribute('data-src') || '') : '';
        lightboxItems.push({
          type: 'video',
          videoSrc: videoSrc,
          poster: posterImg ? (posterImg.currentSrc || posterImg.src) : ''
        });
        return;
      }
      var li = _parseImg(s.html);
      if (!li) return;
      lightboxItems.push({
        type: 'image',
        src: (function(){
          if (li.srcset) {
            var best = null, bestW = 0;
            li.srcset.split(',').forEach(function(entry) {
              var p = entry.trim().split(/\s+/);
              var w = parseInt(p[1], 10) || 0;
              if (w > bestW) { best = p[0]; bestW = w; }
            });
            if (best) return best;
          }
          return li.src.replace(/_(\d+x\d+|\d+x)(\.[a-z]+)/i, '_1600x$2');
        })(),
        thumb: li.src
      });
    });

    // Lock gallery height during rebuild to prevent layout shift
    var prevHeight = gallery.offsetHeight;
    if (prevHeight > 0) {
      gallery.style.minHeight = prevHeight + 'px';
    }

    gallery.innerHTML =
      '<div class="trieye-gallery__main" data-main-idx="0">' + mainSlideHTML + '</div>' +
      (thumbsHTML ? '<div class="trieye-gallery__thumbs">' + thumbsHTML + '</div>' : '') +
      (lifestyleGridHTML ? '<div class="trieye-gallery__lifestyle">' + lifestyleGridHTML + '</div>' : '');

    // Re-attach the Classic PDP "NEW" badge into the hero image cell so it
    // overlays the visible image (innerHTML above just detached it).
    _placePdpNewBadge(gallery, false);

    // Stash lightbox items on the gallery node for openTrieyeLightbox
    gallery.__lightboxItems = lightboxItems;

    // Release the height lock after 2 rAF (aspect-ratio kicks in on first paint)
    requestAnimationFrame(function() {
      requestAnimationFrame(function() {
        gallery.style.minHeight = '';
      });
    });

    // ── Thumbnail click → swap main image + update current index ──
    gallery.querySelectorAll('.trieye-gallery__thumb').forEach(function(thumb, idx) {
      thumb.addEventListener('click', function(e) {
        e.stopPropagation();
        var mainEl = gallery.querySelector('.trieye-gallery__main');
        var mainImgEl = mainEl && mainEl.querySelector('img');
        if (!mainImgEl) return;
        mainImgEl.src = thumb.dataset.src || '';
        mainImgEl.srcset = thumb.dataset.srcset || '';
        mainImgEl.alt = thumb.dataset.alt || '';
        mainEl.setAttribute('data-main-idx', String(idx));
        gallery.querySelectorAll('.trieye-gallery__thumb').forEach(function(t) { t.classList.remove('is-active'); });
        thumb.classList.add('is-active');
      });
    });

    // ── Main click → open lightbox at current main index (tracks active thumb) ──
    var mainContainer = gallery.querySelector('.trieye-gallery__main');
    if (mainContainer) {
      mainContainer.addEventListener('click', function() {
        var idx = parseInt(mainContainer.getAttribute('data-main-idx') || '0', 10);
        openTrieyeLightbox(gallery, idx);
      });
    }

    // ── Lifestyle click → open lightbox at (techCount + lifestyleIdx) ──
    var techCount = thumbSlides.length;
    gallery.querySelectorAll('.trieye-gallery__lifestyle .trieye-gallery__slide').forEach(function(slide, idx) {
      slide.addEventListener('click', function() {
        openTrieyeLightbox(gallery, techCount + idx);
      });
    });

    return;
  }

  // MOBILE: scroll-snap carousel with dots.
  //
  // In-place DOM update, not innerHTML rebuild. The track and slides are
  // reused across variant changes so the browser keeps each <img>'s
  // decoded bitmap — repeat variant clicks become near-instant instead of
  // re-decoding. The HTTP cache also fires because URLs don't change.
  //
  // Loading strategy: slide 0 is eager + fetchpriority=high so it shows
  // ASAP; slides 1+ are lazy + fetchpriority=low so they don't fight for
  // bandwidth. Before this change, 5–7 images loaded in parallel,
  // congesting the connection and delaying the first visible image by
  // 2–3 seconds on mobile networks.

  // Collect image data for the new variant. width/height are pulled from
  // the cached Shopify <img> so the browser can reserve an aspect-ratio
  // box before a lazy image loads — without these, slides expand from
  // ~0 height on load, shifting the page and causing visible jiggle
  // when scrolling near an unloaded slide.
  var imageData = [];
  matchingSlides.forEach(function(s) {
    var tmp = document.createElement('div');
    tmp.innerHTML = s.html;
    var img = tmp.querySelector('img[srcset], img[src]');
    if (img) {
      imageData.push({
        src: img.getAttribute('src') || '',
        srcset: img.getAttribute('srcset') || '',
        alt: img.getAttribute('alt') || '',
        width: img.getAttribute('width') || '800',
        height: img.getAttribute('height') || '800'
      });
    }
  });

  // First-time scaffold: create empty track + dots containers.
  // After this, track is persistent across all variant changes.
  var track = gallery.querySelector('.trieye-gallery__track');
  var dotsContainer = gallery.querySelector('.trieye-gallery__dots');
  if (!track) {
    gallery.innerHTML = '<div class="trieye-gallery__track"></div>' +
      '<div class="trieye-gallery__dots"></div>';
    track = gallery.querySelector('.trieye-gallery__track');
    dotsContainer = gallery.querySelector('.trieye-gallery__dots');
    // Anchor the Classic PDP "NEW" badge to the gallery container so it
    // overlays the visible carousel area at the upper-left corner.
    _placePdpNewBadge(gallery, true);
  }

  // Update or create slides in-place.
  var MOBILE_SIZES = '(max-width: 767px) 100vw, 50vw';
  var existingSlides = track.querySelectorAll('.trieye-gallery__slide');
  imageData.forEach(function(data, idx) {
    var slide = existingSlides[idx];
    var img;
    if (slide) {
      slide.hidden = false;
      // 2026-08-25 (colorway-bleed fix): custom.css declares
      // `.trieye-gallery__slide { display: block }`, which overrides the
      // UA's `[hidden] { display: none }` mapping — so hidden alone never
      // actually hid the surplus slides. Clear/set inline display too.
      slide.style.display = '';
      slide.classList.remove('is-active');
      img = slide.querySelector('img');
    } else {
      slide = document.createElement('div');
      slide.className = 'trieye-gallery__slide';
      img = document.createElement('img');
      img.draggable = false;
      slide.appendChild(img);
      track.appendChild(slide);
    }
    // Only mutate attributes that differ — preserves decoded bitmap when
    // the URL is unchanged.
    if (img.getAttribute('src') !== data.src) img.setAttribute('src', data.src);
    if (img.getAttribute('srcset') !== data.srcset) img.setAttribute('srcset', data.srcset);
    if (img.getAttribute('sizes') !== MOBILE_SIZES) img.setAttribute('sizes', MOBILE_SIZES);
    if (img.getAttribute('alt') !== data.alt) img.setAttribute('alt', data.alt);
    // width/height let the browser compute aspect-ratio and reserve the
    // correct slot height BEFORE the image loads — eliminates the jiggle
    // when scrolling near lazy slides. With CSS height:auto already in
    // place, these attributes only affect the aspect-ratio calculation,
    // not the rendered size.
    if (img.getAttribute('width') !== data.width) img.setAttribute('width', data.width);
    if (img.getAttribute('height') !== data.height) img.setAttribute('height', data.height);
    var loadingAttr = idx === 0 ? 'eager' : 'lazy';
    var priorityAttr = idx === 0 ? 'high' : 'low';
    if (img.getAttribute('loading') !== loadingAttr) img.setAttribute('loading', loadingAttr);
    if (img.getAttribute('fetchpriority') !== priorityAttr) img.setAttribute('fetchpriority', priorityAttr);
  });

  // Hide any extra slides from a previous, larger variant.
  // 2026-08-25 (colorway-bleed fix): the hidden attribute alone is defeated
  // by custom.css's `.trieye-gallery__slide { display: block }` — the old
  // colorway's trailing slides (e.g. Gold lifestyle after switching to
  // Purple) stayed visible at the end of the mobile carousel. Force
  // display:none inline; the attribute is kept as the state marker for
  // the `:not([hidden])` queries below.
  for (var i = imageData.length; i < existingSlides.length; i++) {
    existingSlides[i].hidden = true;
    existingSlides[i].style.display = 'none';
  }

  // Current set of visible slides
  var allSlides = track.querySelectorAll('.trieye-gallery__slide:not([hidden])');
  if (allSlides.length > 0) allSlides[0].classList.add('is-active');

  // Rebuild dots via innerHTML. Cheap, and avoids accumulating click
  // listeners across rebuilds (old elements are removed).
  var dotsHTML = '';
  if (imageData.length > 1) {
    imageData.forEach(function(_, idx) {
      dotsHTML += '<span class="trieye-gallery__dot' + (idx === 0 ? ' is-active' : '') + '" data-idx="' + idx + '"></span>';
    });
  }
  dotsContainer.innerHTML = dotsHTML;
  dotsContainer.hidden = imageData.length <= 1;
  var dots = dotsContainer.querySelectorAll('.trieye-gallery__dot');

  // Reset scroll to image 1 (double-rAF + 150ms safety net inside helper).
  _resetTrackScroll(track);

  // Dot click → smooth scroll to slide.
  dots.forEach(function(dot) {
    dot.addEventListener('click', function() {
      var idx = parseInt(dot.dataset.idx);
      allSlides.forEach(function(s, i) { s.classList.toggle('is-active', i === idx); });
      dots.forEach(function(d, i) { d.classList.toggle('is-active', i === idx); });
      if (track.firstElementChild) {
        track.scrollTo({ left: idx * track.firstElementChild.offsetWidth, behavior: 'smooth' });
      }
    });
  });

  // Active-slide indicator via IntersectionObserver. Fires only on slide
  // crossover (threshold 0.5), not on every scroll frame, so it doesn't
  // compete with iOS's native snap work — eliminates micro-jank.
  //
  // Lazy-initialize once; root = persistent track. observe() is idempotent
  // on already-observed targets. Callback looks up slides/dots fresh from
  // the DOM so it works correctly with the reused-slides model.
  if ('IntersectionObserver' in window && !window._trieyeGalleryIO) {
    window._trieyeGalleryIO = new IntersectionObserver(function(entries) {
      entries.forEach(function(entry) {
        if (!entry.isIntersecting) return;
        var gal = entry.target.closest('.trieye-gallery');
        if (!gal) return;
        var curSlides = gal.querySelectorAll('.trieye-gallery__slide:not([hidden])');
        var curDots = gal.querySelectorAll('.trieye-gallery__dot');
        var idx = Array.prototype.indexOf.call(curSlides, entry.target);
        if (idx < 0) return;
        curSlides.forEach(function(s, i) { s.classList.toggle('is-active', i === idx); });
        curDots.forEach(function(d, i) { d.classList.toggle('is-active', i === idx); });
      });
    }, { root: track, threshold: 0.5 });
  }
  if (window._trieyeGalleryIO) {
    allSlides.forEach(function(slide) { window._trieyeGalleryIO.observe(slide); });
  }

  // ── Mobile tap → fullscreen lightbox (with swipe + pinch-zoom) ──
  // Build a fresh lightbox-items array from this variant's imageData and
  // stash it on the gallery node so openTrieyeLightbox uses it directly
  // (matches the desktop pattern). Pick the largest URL from each srcset
  // for a high-res zoom-quality image; fall back to base src.
  gallery.__lightboxItems = imageData.map(function(d) {
    var fullSrc = d.src;
    if (d.srcset) {
      var best = null, bestW = 0;
      d.srcset.split(',').forEach(function(entry) {
        var p = entry.trim().split(/\s+/);
        var w = parseInt(p[1], 10) || 0;
        if (w > bestW) { best = p[0]; bestW = w; }
      });
      if (best) fullSrc = best;
    }
    return { type: 'image', src: fullSrc, thumb: d.src };
  });
  // Single delegated click handler on the persistent track. Idempotent —
  // only binds once across variant changes (slides are reused, not rebuilt,
  // so listeners would otherwise stack).
  if (!track._trieyeLightboxBound) {
    track._trieyeLightboxBound = true;
    track.addEventListener('click', function(e) {
      var slide = e.target.closest('.trieye-gallery__slide');
      if (!slide) return;
      var visible = Array.prototype.slice.call(track.querySelectorAll('.trieye-gallery__slide:not([hidden])'));
      var idx = visible.indexOf(slide);
      if (idx >= 0) openTrieyeLightbox(gallery, idx);
    });
  }
}

/**
 * Called once from each snippet's DOMContentLoaded.
 * Triggers gallery filter — no longer needs to wait for Flickity.
 */
function initGalleryAfterFlickity() {
  // No longer depends on Flickity — trigger gallery filter immediately
  _lastGalleryFilter = '';
  if (typeof state !== 'undefined' && state.color) {
    filterGalleryByColor(state.color);
  }

  // Reset mobile carousel to image 1 on bfcache restore. When the user
  // navigates back/forward on iOS Safari, DOMContentLoaded does NOT fire
  // but the scrollLeft of the inner track is preserved — the carousel
  // would open on whatever image they last viewed. The guard prevents
  // double-binding if this function is called more than once per page.
  if (!window._trieyeGalleryPageshowBound) {
    window._trieyeGalleryPageshowBound = true;
    window.addEventListener('pageshow', function(e) {
      if (!e.persisted) return;
      _resetTrackScroll(document.querySelector('.trieye-gallery__track'));
      var slides = document.querySelectorAll('.trieye-gallery__slide');
      var dots = document.querySelectorAll('.trieye-gallery__dot');
      slides.forEach(function(s, i) { s.classList.toggle('is-active', i === 0); });
      dots.forEach(function(d, i) { d.classList.toggle('is-active', i === 0); });
    });
  }
}

/**
 * Mobile layout: move title + lens type selector above gallery.
 * Order: Title+Price → Lens selector → Gallery → Rest of configurator
 * Called once from each snippet's DOMContentLoaded.
 */
function initMobileLensPosition() {
  if (window.innerWidth <= 767) {
    var mediaWrapper = document.querySelector('.product__media__wrapper');
    if (!mediaWrapper) return;

    // Move title+price above gallery
    var titleBlock = document.querySelector('.title-price-wrapper');
    // 2026-10-28 (CLS): the class is now rendered server-side (sections/product.liquid), so decide the move by
    // position instead of by class. Still idempotent.
    if (titleBlock && titleBlock.parentElement !== mediaWrapper) {
      mediaWrapper.insertBefore(titleBlock, mediaWrapper.firstChild);
      titleBlock.classList.add('trieye-mobile-title');
    }
    // 2026-05-14: move the mirror strip (rendered in .product__inner on
    // desktop) into the media wrapper so it sits above the gallery on
    // mobile (between title and hero image). Idempotent.
    var mirrorStrip = document.querySelector('.view-mirror-strip');
    if (mirrorStrip && mirrorStrip.parentElement !== mediaWrapper) {
      // Insert AFTER the title block so order is: title → strip → gallery.
      var anchor = titleBlock || mediaWrapper.firstChild;
      if (anchor && anchor.parentElement === mediaWrapper) {
        anchor.insertAdjacentElement('afterend', mirrorStrip);
      } else {
        mediaWrapper.insertBefore(mirrorStrip, mediaWrapper.firstChild);
      }
    }
    // .px-lens deliberately NOT moved — it stays below the gallery so the
    // hero image sits directly under the title.

    // Inject Model line between the title and the price.
    var title = document.querySelector('.trieye-mobile-title .product__title');
    if (title && !document.querySelector('.trieye-mobile-title [data-model-line]')) {
      var modelLine = document.createElement('span');
      modelLine.className = 'px-model-line';
      modelLine.setAttribute('data-model-line', '');
      title.insertAdjacentElement('afterend', modelLine);
    }

    // 2026-05-12 (Quieter): the View snippet now wraps the size label
    // and the Size Guide button in a `.px-size-info-row` flex container
    // with `justify-content: space-between`. When that wrapper exists,
    // the button is ALREADY where it should be (right-aligned within
    // the row) and we MUST NOT move it into `.px-size-info` — doing
    // so was nesting it inside the label text and dropping its visual
    // distinction. For products that still have the legacy flat
    // structure (Clip uses .px-model-header, Spare Lens uses a similar
    // layout), keep the old move-into-info behavior.
    //
    // 2026-05-14: the View snippet ALSO ships a NEW inline Size Guide
    // button in the CHOOSE MODEL header row (`.sizeguidebtn--inline`,
    // inside `.px-title-row--with-legend`). That button is where the
    // header-row Size Guide lives now — never touch it; only consider
    // the legacy `.sizeguidebtn` inside `.px-size-info-row`.
    var sizeGuide = document.querySelector('.px-wrap .sizeguidebtn:not([data-size-guide-hidden]):not(.sizeguidebtn--inline)');
    var alreadyInRow = sizeGuide && sizeGuide.closest('.px-size-info-row');
    if (!alreadyInRow) {
      var sizeInfo = document.querySelector('.px-wrap .px-size-info');
      if (sizeGuide && sizeInfo && !sizeInfo.contains(sizeGuide)) {
        sizeInfo.appendChild(sizeGuide);
      }
    }

    updateModelLine();
  }
}

function updateModelLine() {
  var els = document.querySelectorAll('[data-model-line]');
  if (!els.length) return;

  // 2026-05-12 (Quieter): model line is now formatted HTML, not plain
  // text. The new design has a label + a "name" half (color/lens
  // chained with slashes) + a "spec" half (mirror/size). Slashes
  // separate components within each half; a thin vertical divider
  // sits between the two halves.
  //
  //   MODEL  Matte Black/Polarized Smoke  |  Left/M145
  //
  // Helper writes the same HTML to every model-line element.
  var setHTML = function(html) {
    els.forEach(function(el) { el.innerHTML = html; });
  };
  // Backward-compat: kept as a no-op so legacy call sites don't break.
  var setText = function(t) {
    setHTML(t ? escapeHtml(t) : '');
  };
  function escapeHtml(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // Preferred source: the active color swatch button. View/Classic have both
  // data-color (option2 = frame color) and data-lens (option1 = full lens
  // type + color + cat). Clip has only data-lens. Parent class determines size.
  var activeColorBtn = document.querySelector('.px-btn.active[data-lens]');

  if (!activeColorBtn) {
    // Products without a color swatch (e.g. Spare Lens) — fall back to the
    // active lens-group label.
    var activeLens = document.querySelector('.px-group.active');
    var label = activeLens ? activeLens.querySelector('.lens-card__name') : null;
    setText(label ? 'Model: ' + label.textContent.trim() : '');
    return;
  }

  var color = activeColorBtn.getAttribute('data-color') || '';
  var lens = activeColorBtn.getAttribute('data-lens') || '';

  var size = '';
  var sizeMm = '';
  var readMm = function(info) {
    if (!info) return '';
    var span = info.querySelector('span');
    var src = span || info;
    return (src.textContent.match(/\d+/) || [''])[0];
  };
  // Read size label from the visible .px-size-info text (e.g. "Medium (145mm)",
  // "Small (140mm)", "One Size (140mm)") so we don't hard-code Small for any
  // single-size product (Classic uses .px-color-small but the actual label is
  // "One Size", not "Small" — assuming Small misled customers).
  var readLabel = function(info) {
    if (!info) return '';
    var clone = info.cloneNode(true);
    // Strip the (mm) span so only the label text remains
    var span = clone.querySelector('span');
    if (span) span.remove();
    // Strip the Size Guide button (JS moves it into .px-size-info on mobile —
    // without this, its text leaked into the model line as "MEDIUM SIZE GUIDE")
    var btn = clone.querySelector('.sizeguidebtn');
    if (btn) btn.remove();
    return clone.textContent.trim();
  };
  var sizeInfo = null;
  if (activeColorBtn.closest('.px-color-medium')) {
    sizeInfo = document.querySelector('.px-wrap .px-size-info');
  } else if (activeColorBtn.closest('.px-color-small')) {
    sizeInfo = document.querySelector('.px-size-section-small .px-size-info')
            || document.querySelector('.px-wrap .px-size-info');
  }
  if (sizeInfo) {
    size = readLabel(sizeInfo);
    sizeMm = readMm(sizeInfo);
  }

  // 2026-05-11 (L5 + L17): include the active mirror side in the model line.
  // Read from [data-mirror].active. The value (e.g. "Left Mirror") already
  // contains the word "Mirror" because data-mirror is parsed from the
  // variant title — don't double it (the original L5 produced "Left Mirror
  // Mirror"). Fallback adds " Mirror" only if missing, so any future product
  // whose data-mirror is just "Left" still reads correctly.
  var activeMirrorBtn = document.querySelector('[data-mirror].active');
  var mirror = activeMirrorBtn ? (activeMirrorBtn.getAttribute('data-mirror') || '').trim() : '';
  var mirrorLabel = '';
  if (mirror) {
    mirrorLabel = /\bmirror\b/i.test(mirror) ? mirror : mirror + ' Mirror';
  }

  // 2026-05-12 (Quieter): compose the new "MODEL  COLOR/LENS · SIDE/SIZE"
  // formatted HTML. Each half is a chain of components joined with `/`;
  // the two halves are split by a thin vertical divider rendered via
  // CSS (.px-model-line__spec border-left).
  //
  // Examples:
  //   MODEL  Matte Black/Polarized Smoke  |  Left/M145
  //   MODEL  Polarized Smoke  |  Left/M145         (Spare Lens: no color)
  //   MODEL  Matte Black/HC Smoke                  (Classic: no size, no mirror)

  // Short mirror — drop "Mirror" suffix for the model line
  var mirrorShort = '';
  if (mirror) {
    mirrorShort = mirror.replace(/\s*mirror\b/i, '').trim();
  }
  // Short size — "M145" / "S140" instead of "Medium (145mm)"
  var sizeShort = '';
  if (size && sizeMm) {
    var sizeLetter = /^small/i.test(size) ? 'S' : /^medium/i.test(size) ? 'M' : size.charAt(0).toUpperCase();
    sizeShort = sizeLetter + sizeMm;
  } else if (sizeMm) {
    sizeShort = sizeMm;
  }

  var nameParts = [];
  if (color) nameParts.push(escapeHtml(color));
  if (lens) nameParts.push(escapeHtml(lens));
  var nameHtml = nameParts.join('<span class="px-model-line__sep">/</span>');

  var specParts = [];
  if (mirrorShort) specParts.push(escapeHtml(mirrorShort));
  if (sizeShort) specParts.push(escapeHtml(sizeShort));
  var specHtml = specParts.join('<span class="px-model-line__sep">/</span>');

  if (!nameHtml && !specHtml) {
    setHTML('');
    return;
  }

  var html = '<span class="px-model-line__label">' + (window.__configLabels && window.__configLabels.model || 'Model') + '</span>';
  if (nameHtml) html += '<span class="px-model-line__name">' + nameHtml + '</span>';
  if (specHtml) html += '<span class="px-model-line__spec">' + specHtml + '</span>';
  setHTML(html);
}

/* ═══════════════════════════════════════════
   PRICE FORMATTING
   ═══════════════════════════════════════════ */

/**
 * Format cents to localized money string using theme.moneyFormat.
 * Defined here as default — snippets can override with their own version.
 */
if (typeof formatMoneyDisplay !== 'function') {
  // Pure formatter: converts cents → theme-aware money string with NO side
  // effects. Use this for per-item prices (addon cards, line items) where you
  // do NOT want to touch [data-button-price]. formatMoneyClean() wraps this
  // and adds the button-price side-effect for the grand total.
  window.formatMoneyDisplay = function(cents) {
    try {
      var amount = Number(cents);
      var decimal = (amount / 100).toFixed(2);
      var noDecimals = Math.round(amount / 100).toString();
      var parts = decimal.split('.');
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      var withComma = parts[0] + ',' + parts[1];
      var noDecimalsComma = noDecimals.replace(/\B(?=(\d{3})+(?!\d))/g, '.');

      if (typeof theme === "undefined" || !theme.moneyFormat || typeof theme.moneyFormat !== "string") {
        return withComma;
      }

      var formatted = theme.moneyFormat
        .replace("{{amount_with_comma_separator}}", withComma)
        .replace("{{amount_no_decimals_with_comma_separator}}", noDecimalsComma)
        .replace("{{amount_no_decimals}}", noDecimals)
        .replace("{{amount}}", decimal);
      return formatted.replace(/<[^>]*>/g, '');
    } catch (error) {
      return "0,00";
    }
  };
}

if (typeof formatMoneyClean !== 'function') {
  window.formatMoneyClean = function(cents) {
    var formatted = formatMoneyDisplay(cents);
    try {
      var amount = Number(cents);
      var decimal = (amount / 100).toFixed(2);
      if (typeof updateButtonPrice === 'function') updateButtonPrice(decimal);
      if (typeof updateSavingsDisplay === 'function') updateSavingsDisplay();
    } catch (error) { /* noop */ }
    return formatted;
  };
}

if (typeof updateButtonPrice !== 'function') {
  window.updateButtonPrice = function(basePrice) {
    try {
      var currency = theme.currencyCode || "USD";
      var el = document.querySelector("[data-button-price]");
      if (el) el.textContent = currency + ' ' + basePrice;
    } catch (err) {
      var el2 = document.querySelector("[data-button-price]");
      if (el2) el2.textContent = basePrice;
    }
  };
}

/**
 * Keep the top sale-price-block in sync with the active variant.
 *
 * Each variant snippet (the-classic / the-view / the-clip / the-spare-lens
 * variants-2026.liquid) dispatches a `variant:change` CustomEvent on
 * `document` whenever the user picks a new lens/color/mirror. The bottom
 * cart button has its own update path via updateButtonPrice(), but the TOP
 * .sale-price-block snippet was only ever rendered server-side from
 * `product.selected_or_first_available_variant.price` — it never reacted
 * to client-side variant changes. Result: visiting /products/the-classic
 * showed the cheapest variant's price up top, then clicking through type
 * options updated the bottom button but left the top frozen.
 *
 * One listener here covers all 4 PDPs because the event is dispatched
 * uniformly. Handles both regular and on-sale price blocks.
 */
document.addEventListener('variant:change', function (e) {
  var v = e && e.detail && e.detail.variant;
  if (!v) return;
  if (typeof formatMoneyDisplay !== 'function') return;

  var priceFmt = formatMoneyDisplay(v.price);
  var compareFmt = (v.compare_at_price && v.compare_at_price > v.price)
    ? formatMoneyDisplay(v.compare_at_price)
    : null;

  // Regular (non-sale) price element
  var nowReg = document.querySelector('.sale-price-block__now--regular');
  if (nowReg) nowReg.innerHTML = '<span class="money">' + priceFmt + '</span>';

  // Sale-active price (different class — no --regular suffix when on_sale=true)
  var blocks = document.querySelectorAll('.sale-price-block');
  blocks.forEach(function (block) {
    if (block.getAttribute('data-on-sale') !== 'true') return;
    var nowSale = block.querySelector('.sale-price-block__now:not(.sale-price-block__now--regular)');
    if (nowSale) nowSale.innerHTML = '<span class="money">' + priceFmt + '</span>';
    var strike = block.querySelector('.sale-price-block__strike');
    if (strike && compareFmt) strike.innerHTML = '<span class="money">' + compareFmt + '</span>';
  });
});

/**
 * Show / hide the "You save" line under the cart button.
 *
 * Reads window.currentVariantCompareAt (set by each variant snippet when the
 * variant changes) and currentVariantPrice. If compare_at > price, computes
 * savings on (base × qty) + addon prices (addon-level savings already render
 * via .addon-card__badge, so we compare only the base discount).
 *
 * Auto-invoked from formatMoneyClean() so any price update triggers a refresh.
 */
if (typeof updateSavingsDisplay !== 'function') {
  window.updateSavingsDisplay = function() {
    var wrap = document.querySelector('[data-savings]');
    if (!wrap) return;
    var compareAtBase = Number(window.currentVariantCompareAt) || 0;
    var priceBase = Number(window.currentVariantPrice) || 0;
    if (compareAtBase <= priceBase || priceBase <= 0) {
      wrap.setAttribute('hidden', '');
      return;
    }
    var qty = (typeof getProductQuantity === 'function') ? getProductQuantity() : 1;
    var addonTotal = 0;
    if (typeof selectedAddons !== 'undefined' && Array.isArray(selectedAddons)) {
      // 2026-05-12 (Quieter): respect per-addon quantity from the stepper.
      selectedAddons.forEach(function(a){
        var aq = Math.max(1, parseInt(a.quantity, 10) || 1);
        addonTotal += (parseFloat(a.price) || 0) * aq;
      });
    }
    var priceCents = priceBase * qty + addonTotal;
    var compareCents = compareAtBase * qty + addonTotal;
    var diff = compareCents - priceCents;
    if (diff <= 0) { wrap.setAttribute('hidden', ''); return; }
    var pct = Math.round((diff / compareCents) * 100);
    var amountEl = wrap.querySelector('[data-savings-amount]');
    var pctEl = wrap.querySelector('[data-savings-pct]');
    if (amountEl) amountEl.textContent = formatMoneyDisplay(diff);
    if (pctEl) pctEl.textContent = '(' + pct + '%)';
    wrap.removeAttribute('hidden');
  };
}

/**
 * Read the current quantity from the product form's quantity input.
 * Falls back to 1 if no input or invalid value.
 */
function getProductQuantity() {
  var form = document.querySelector('form[action*="/cart/add"]');
  var qtyInput = null;
  if (form) {
    qtyInput = form.querySelector('[name="quantity"]');
    if (!qtyInput && form.id) {
      qtyInput = document.querySelector('input[name="quantity"][form="' + form.id + '"]');
    }
  }
  if (!qtyInput) {
    qtyInput = document.querySelector('.quantity__input[name="quantity"]');
  }
  return qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
}

/**
 * Update config accordion — single source of truth across all 4 PDPs.
 * Each snippet sets window.__configContext.mode and window.__configLabels
 * (with Liquid-rendered translated strings) before this file evals.
 *
 * Mode controls section header label and which extra rows render:
 *   'view'       → "Glasses" header
 *   'classic'    → product-title header + "Spare lens (no mirror) included" line
 *   'clip'       → product-title header
 *   'spare-lens' → product-title header + omits "Frame" row (color is always "Spare Lens")
 */
if (typeof updateConfig !== 'function') {
  window.updateConfig = function() {
    var container = document.querySelector(".config-items");
    var priceEl = document.querySelector(".config-price");
    if (!container || !priceEl) return;

    var L = window.__configLabels || {};
    var ctx = window.__configContext || {};
    var mode = ctx.mode || 'view';

    container.innerHTML = "";
    // 2026-05-12 (Quieter, eyewear-qty fix): the eyewear stepper at the
    // bottom of the configurator changes how many frames go in the cart,
    // but the YOUR BUILD subtotal + composite row used to ignore it (the
    // customer saw "$169" with qty=2). Read qty ONCE at the top and use
    // it both in the composite line ("× N" badge + price × qty) and the
    // accumulator that drives Subtotal / Savings / the Add-to-Cart
    // button. Addons keep their OWN per-addon stepper qty (we don't
    // multiply addons by eyewear qty — cart-side they go in at addonQty,
    // not addonQty × eyewearQty).
    var prodQty = (typeof getProductQuantity === 'function') ? getProductQuantity() : 1;
    prodQty = Math.max(1, parseInt(prodQty, 10) || 1);
    var eyewearUnitCents = (typeof currentVariantPrice === 'number') ? currentVariantPrice : 0;
    var eyewearLineCents = eyewearUnitCents * prodQty;
    var total = eyewearLineCents;

    if (typeof state !== 'undefined') {
      // Section header label — defaults to "Glasses" for View, falls through to per-product title
      var sectionLabel = (mode === 'view')
        ? (L.glasses || 'Glasses')
        : (ctx.productTitle || L.glasses || 'Glasses');

      // Unified size label: "Medium (145mm)" / "Small (140mm)".
      // Classic is one-size — the row would imply a choice that doesn't exist,
      // so it's omitted in 'classic' mode regardless of what state.size holds.
      var sizeLabel = '';
      if (state.size && mode !== 'classic') {
        if (state.size.indexOf('145') !== -1) sizeLabel = (L.size_medium || 'Medium (145mm)');
        else if (state.size.indexOf('140') !== -1) sizeLabel = (L.size_small || 'Small (140mm)');
        else sizeLabel = state.size;
      }

      // Frame row — omitted when color is meaningless (Spare Lens hardcodes "Spare Lens")
      var frameRow = (mode === 'spare-lens' || !state.color || state.color === 'Spare Lens')
        ? ''
        : '<div class="config-row"><span class="config-key">' + (L.frame || 'Frame') + '</span><span class="config-val">' + state.color + '</span></div>';

      // Classic-only: "Spare lens (no mirror) included" line + addon-card label side-effect
      var spareLensRow = '';
      if (mode === 'classic' && state.mirror) {
        var mirrorSide = /left|venstre/i.test(state.mirror) ? (L.left || 'Left')
                       : /right|h.yre/i.test(state.mirror) ? (L.right || 'Right')
                       : '';
        if (mirrorSide) {
          var spareTpl = (L.spare_lens_included || '{side} spare lens (no mirror) included');
          var spareLine = spareTpl.replace('{side}', mirrorSide);
          spareLensRow = '<div class="config-row"><span class="config-key">' + (L.included || 'Included') + '</span><span class="config-val">' + spareLine + '</span></div>';
          // Side-effect: keep the addon-card label in sync with the chosen mirror side
          var spareLabel = document.querySelector('[data-spare-lens-label]');
          var spareCard = document.querySelector('.addon-category-btn--included');
          if (spareLabel) spareLabel.textContent = mirrorSide + ' ' + (L.spare_lens_no_mirror || 'spare lens (no mirror)');
          if (spareCard) spareCard.dataset.includedSide = mirrorSide.toLowerCase();
        }
      }

      // 2026-05-12 (Quieter): YOUR BUILD inside — composite glasses row.
      // Replaces the previous 4 separate rows (Lens / Frame / Size /
      // Mirror) with ONE row showing:
      //   <b>Polarized Smoke</b>
      //   Matte Black · Left mirror · Medium (145mm)         €169
      //
      // The composite is much more scannable than the 4-attribute list
      // and matches the YOUR BUILD design approved in the brainstorm.
      var mirrorShortForBuild = state.mirror
        ? String(state.mirror).replace(/\s*mirror\b/i, '').trim()
        : '';
      // 2026-05-14: append market context next to the mirror side so
      // customers see at a glance which traffic context their chosen
      // side suits. Dual gets no market because both sides are covered.
      var mirrorMarket = '';
      if (/^left$/i.test(mirrorShortForBuild)) mirrorMarket = ' (US / Europe)';
      else if (/^right$/i.test(mirrorShortForBuild)) mirrorMarket = ' (UK / Australia)';
      var metaParts = [];
      if (state.color && mode !== 'spare-lens' && state.color !== 'Spare Lens') metaParts.push(state.color);
      if (mirrorShortForBuild) metaParts.push(mirrorShortForBuild + ' mirror' + mirrorMarket);
      if (sizeLabel) metaParts.push(sizeLabel);
      var compositeMeta = metaParts.join(' · ');
      // Reuse eyewearLineCents (already × prodQty) so the displayed price
      // matches what's actually charged. When qty > 1 the composite title
      // also gets a "× N" badge — same visual pattern as addon lines.
      var lensTitleHTML = '<b>' + (state.lens || '') + '</b>'
        + (prodQty > 1 ? ' <span class="config-eyewear-qty">× ' + prodQty + '</span>' : '');

      // v96 (2026-07-30): show the eyewear's own sale strike on the composite
      // row. The struck Subtotal + "You save" already counted the eyewear's
      // compare-at discount, but the glasses LINE showed only the final price
      // — the per-line saving was invisible (user feedback during the View
      // Originals -10% sale). Same struck-original pattern as addon lines.
      var eyewearOrigUnit = (typeof window.currentVariantCompareAt === 'number'
        && window.currentVariantCompareAt > (window.currentVariantPrice || 0))
        ? window.currentVariantCompareAt : 0;
      var eyewearPriceCell;
      if (eyewearOrigUnit > 0) {
        eyewearPriceCell =
          '<span class="config-val-wrap">' +
            '<span class="config-val-orig">' + formatMoneyClean(eyewearOrigUnit * prodQty) + '</span>' +
            '<span class="config-val">' + formatMoneyClean(eyewearLineCents) + '</span>' +
          '</span>';
      } else {
        eyewearPriceCell = '<span class="config-val">' + formatMoneyClean(eyewearLineCents) + '</span>';
      }
      container.innerHTML +=
        '<div class="config-section-label">' + sectionLabel + '</div>' +
        '<div class="config-row config-row--composite">' +
          '<span class="config-key">' +
            lensTitleHTML +
            (compositeMeta ? '<br>' + compositeMeta : '') +
          '</span>' +
          eyewearPriceCell +
        '</div>' +
        spareLensRow;

      // Subtotal moves AFTER addons (see below). The previous code
      // emitted Subtotal here showing only the eyewear price — which
      // was misleading once addons were added.
    }

    if (typeof selectedAddons !== 'undefined' && selectedAddons.length > 0) {
      container.innerHTML += '<div class="config-divider"></div>' +
        '<div class="config-section-label">' + (L.add_ons || 'Add-ons') + '</div>';
      // 2026-05-11 (L25): per-addon discount display in the PDP YOUR BUILD
      // accordion. Mirrors the sticky drawer treatment (struck original
      // before the current price) so discounted addons read correctly
      // here too. Addons carry originalCents / discountPct via the
      // updated syncSelectedAddons (View snippet); other product snippets
      // not yet updated fall back gracefully (no original price, no
      // chip — same as before).
      selectedAddons.forEach(function(a) {
        // 2026-05-12 (Quieter): addons may carry quantity > 1 from the
        // accessory stepper. Multiply price by qty for the line subtotal
        // and append "× N" to the title.
        var qty = Math.max(1, parseInt(a.quantity, 10) || 1);
        var curCents = (typeof a.currentCents === 'number') ? a.currentCents : parseFloat(a.price) || 0;
        var origCents = (typeof a.originalCents === 'number') ? a.originalCents : curCents;
        var curLine = curCents * qty;
        var origLine = origCents * qty;
        var priceCellHTML;
        if (origLine > curLine) {
          priceCellHTML =
            '<span class="config-val-wrap">' +
              '<span class="config-val-orig">' + formatMoneyClean(origLine) + '</span>' +
              '<span class="config-val">+' + formatMoneyClean(curLine) + '</span>' +
            '</span>';
        } else {
          priceCellHTML =
            '<span class="config-val-wrap">' +
              '<span class="config-val">+' + formatMoneyClean(curLine) + '</span>' +
            '</span>';
        }
        var titleHTML = a.title + (qty > 1 ? ' <span class="config-addon-qty">× ' + qty + '</span>' : '');
        container.innerHTML += '<div class="config-row"><span class="config-key">' + titleHTML +
          (a.variantInfo ? '<span class="config-variant-info"> · ' + a.variantInfo + '</span>' : '') +
          '</span>' + priceCellHTML + '</div>';
        total += curLine;
      });
    }

    // 2026-05-12 (Quieter): Subtotal row — moved from inside the
    // glasses section to AFTER addons. Now shows the FULL total
    // (eyewear + all addons) instead of just the eyewear price.
    // If any addon has a discount, also show a struck pre-discount
    // total so the savings are visible at a glance.
    if (typeof state !== 'undefined') {
      // Eyewear pre-discount basis, × prodQty so the struck Subtotal
      // matches the qty-multiplied current Subtotal below it.
      var eyewearOriginalUnit = (typeof window.currentVariantCompareAt === 'number'
        && window.currentVariantCompareAt > (window.currentVariantPrice || 0))
        ? window.currentVariantCompareAt
        : (window.currentVariantPrice || 0);
      var subtotalOriginal = eyewearOriginalUnit * prodQty;
      if (typeof selectedAddons !== 'undefined' && selectedAddons.length) {
        selectedAddons.forEach(function (a) {
          var aq = Math.max(1, parseInt(a.quantity, 10) || 1);
          var c = (typeof a.currentCents === 'number') ? a.currentCents : (parseFloat(a.price) || 0);
          var o = (typeof a.originalCents === 'number') ? a.originalCents : c;
          subtotalOriginal += o * aq;
        });
      }
      var subtotalRowVal = (subtotalOriginal > total)
        ? '<span class="config-val-wrap">' +
            '<span class="config-val-orig">' + formatMoneyClean(subtotalOriginal) + '</span>' +
            '<span class="config-val">' + formatMoneyClean(total) + '</span>' +
          '</span>'
        : '<span class="config-val">' + formatMoneyClean(total) + '</span>';
      container.innerHTML +=
        '<div class="config-row config-row--subtotal">' +
          '<span class="config-key">' + (L.subtotal || 'Subtotal') + '</span>' +
          subtotalRowVal +
        '</div>';
    }

    // 2026-05-11 (L30 / shared across all 4 PDPs): "You save X" line
    // after the addon stack. Sums savings across the eyewear variant
    // (compare_at_price > price) and every addon's discount delta
    // (originalCents - currentCents). Was previously only in the View
    // snippet's wrapUpdateConfig; moving it here means Classic + Clip +
    // Spare Lens get the same treatment automatically.
    //
    // The View's wrapUpdateConfig adds its OWN savings row covering the
    // lens picker too (which Classic/Clip don't have). To avoid two
    // rows for View, we tag this base one with a different data attr
    // (`data-savings-row-base`) — View's wrapper strips it and renders
    // its own broader row.
    try {
      var eyewearOriginal = (typeof window.currentVariantCompareAt === 'number'
        && window.currentVariantCompareAt > (window.currentVariantPrice || 0))
        ? window.currentVariantCompareAt
        : (window.currentVariantPrice || 0);
      var eyewearCurrent = (typeof window.currentVariantPrice === 'number')
        ? window.currentVariantPrice
        : 0;
      var addonSavings = 0;
      if (typeof selectedAddons !== 'undefined' && selectedAddons.length) {
        selectedAddons.forEach(function (a) {
          var aq = Math.max(1, parseInt(a.quantity, 10) || 1);
          var c = (typeof a.currentCents === 'number') ? a.currentCents : (parseFloat(a.price) || 0);
          var o = (typeof a.originalCents === 'number') ? a.originalCents : c;
          if (o > c) addonSavings += (o - c) * aq;
        });
      }
      // Eyewear savings scale with prodQty (buying 2 saves twice as much);
      // addon savings already factored each addon's own qty above.
      var totalSavings = (eyewearOriginal - eyewearCurrent) * prodQty + addonSavings;
      if (totalSavings > 0) {
        container.innerHTML +=
          '<div class="config-row config-row--savings" data-savings-row-base="1">' +
            '<span class="config-key">' + (L.you_save || 'You save') + '</span>' +
            '<span class="config-val-wrap">' +
              '<span class="config-val config-val--savings">' + formatMoneyClean(totalSavings) + '</span>' +
            '</span>' +
          '</div>';
      }
    } catch (e) { /* fail silently — savings row is decorative */ }

    // Set config panel total (now line-total: eyewear × qty + addons × addonQty)
    priceEl.innerHTML = formatMoneyClean(total);

    // 2026-05-12 (Quieter, eyewear-qty fix): button-price is now `total`
    // directly. `total` already includes eyewearLineCents (eyewear ×
    // prodQty) + each addon × addonQty, which matches exactly what
    // /cart/add.js receives (main qty=prodQty + each addon at addonQty,
    // NOT multiplied by prodQty). The OLD `total × qty` over-charged the
    // addons by a factor of prodQty whenever qty > 1.
    formatMoneyClean(total); // panel-formatting side-effect (savings display, etc.)
    if (typeof updateButtonPrice === 'function') {
      var dollarStr = (Number(total) / 100).toFixed(2);
      updateButtonPrice(dollarStr);
    }

    // Refresh the mobile Model line (no-op on desktop where the element doesn't exist)
    if (typeof updateModelLine === 'function') updateModelLine();

    // 2026-05-12 (Quieter): write YOUR BUILD closed-state contextual hint.
    // Replaces the generic "Tap to see what's in your build" with a
    // glanceable summary: "Matte Black/Polarized Smoke · Left/M145 +1 add-on".
    if (typeof state !== 'undefined') {
      var hintEls = document.querySelectorAll('[data-config-header-hint]');
      if (hintEls.length) {
        // Reuse the same short-mirror / short-size logic as the model line
        var mirrorShortForHint = state.mirror
          ? String(state.mirror).replace(/\s*mirror\b/i, '').trim()
          : '';
        var sizeShortForHint = '';
        if (state.size) {
          var sizeStr = String(state.size);
          var sizeMmMatch = sizeStr.match(/\d{3}/);
          var sizeMm2 = sizeMmMatch ? sizeMmMatch[0] : '';
          // 2026-05-14: Classic is one-size — its size 140 should map to
          // "O140" (matches the CHOOSE MODEL legend pill 'O = One size
          // (140 mm)'), not "S140" which would imply Small / part of a
          // multi-size range that doesn't exist for Classic.
          var sizeLetter2 = '';
          if (mode === 'classic' || /one\s*size/i.test(sizeStr)) sizeLetter2 = 'O';
          else if (/140|small/i.test(sizeStr)) sizeLetter2 = 'S';
          else if (/145|medium/i.test(sizeStr)) sizeLetter2 = 'M';
          sizeShortForHint = (sizeLetter2 && sizeMm2) ? (sizeLetter2 + sizeMm2) : (sizeMm2 || sizeLetter2);
        }
        var nameSide = [];
        if (state.color && mode !== 'spare-lens' && state.color !== 'Spare Lens') nameSide.push(state.color);
        if (state.lens) nameSide.push(state.lens);
        var specSide = [];
        if (mirrorShortForHint) specSide.push(mirrorShortForHint);
        if (sizeShortForHint) specSide.push(sizeShortForHint);

        var hintHTML = '';
        if (nameSide.length) {
          hintHTML += '<b>' + nameSide.join('<span class="hint-sep">/</span>') + '</b>';
        }
        if (specSide.length) {
          hintHTML += (hintHTML ? '<span class="hint-sep"> · </span>' : '') +
            specSide.join('<span class="hint-sep">/</span>');
        }
        // Add-on count badge in green
        var addonCount = (typeof selectedAddons !== 'undefined') ? selectedAddons.length : 0;
        if (addonCount > 0) {
          hintHTML += '<span class="hint-addons">+' + addonCount + ' add-on' + (addonCount > 1 ? 's' : '') + '</span>';
        }
        if (!hintHTML) {
          hintHTML = 'Tap to see what\'s in your build';
        }
        hintEls.forEach(function(el) { el.innerHTML = hintHTML; });
      }
    }
  };
}

/* Listen for quantity changes → re-run updateConfig to update button price */
(function() {
  // Use event delegation since the quantity input may be created dynamically
  document.addEventListener('change', function(e) {
    if (e.target && e.target.name === 'quantity') {
      if (typeof updateConfig === 'function') updateConfig();
    }
  });
  // Also catch +/- button clicks (stepper dispatches 'change' but also 'input')
  document.addEventListener('input', function(e) {
    if (e.target && e.target.name === 'quantity') {
      if (typeof updateConfig === 'function') updateConfig();
    }
  });
})();

/* ═══════════════════════════════════════════
   ADDON FILTERING
   ═══════════════════════════════════════════ */

function filterAddonsByMirror() {
  // If the snippet defines updateAddonCards (drawer-based addons), use that instead
  if (typeof updateAddonCards === 'function') {
    updateAddonCards();
    if (typeof updateConfig === 'function') updateConfig();
    return;
  }

  // Fallback: simple mirror-based filtering for non-drawer addons
  var mirror = state.mirror ? state.mirror.toLowerCase() : '';
  var cards = document.querySelectorAll(".addon-card");
  var mirrorButtons = document.querySelectorAll(".px-mirror-row [data-mirror]");
  var hasMirrorUI = mirrorButtons.length > 0;

  cards.forEach(function(card) {
    var title = (card.querySelector("strong")?.innerText || '').toLowerCase();
    card.classList.remove("active");

    if (!hasMirrorUI || !mirror) {
      card.style.display = "";
      return;
    }

    card.style.display = title.includes(mirror) ? "" : "none";
  });

  selectedAddons = [];
  if (typeof updateConfig === 'function') updateConfig();
}

// ============================================================
// Spec section — MM / INCH unit toggle
// ============================================================
window.initSpecUnitToggle = function initSpecUnitToggle(rootEl) {
  var root = rootEl || document;
  var toggles = root.querySelectorAll('[data-spec-unit-toggle]');
  if (!toggles.length) return;

  toggles.forEach(function(toggle) {
    if (toggle._specUnitBound) return;
    toggle._specUnitBound = true;

    toggle.addEventListener('click', function(e) {
      var btn = e.target.closest('[data-unit]');
      if (!btn) return;
      var unit = btn.getAttribute('data-unit');
      if (!unit) return;

      toggle.querySelectorAll('[data-unit]').forEach(function(b) {
        b.classList.toggle('is-active', b === btn);
      });

      var scope = toggle.closest('[data-spec-measurements]') || document;
      var dims = scope.querySelectorAll('.spec-dim[data-mm]');
      dims.forEach(function(dim) {
        var mm = parseFloat(dim.getAttribute('data-mm'));
        if (isNaN(mm)) return;
        if (unit === 'inch') {
          var inches = mm / 25.4;
          dim.textContent = inches.toFixed(1);
        } else {
          dim.textContent = String(mm);
        }
      });
    });
  });
};

document.addEventListener('DOMContentLoaded', function() { window.initSpecUnitToggle(); });

// ============================================================
// Spec wireframe swap — The View only (single <-> dual mirror)
// ============================================================
window.initSpecWireframeSwap = function initSpecWireframeSwap() {
  var wrap = document.querySelector('[data-spec-wireframe]');
  if (!wrap) return;
  var handle = wrap.getAttribute('data-product-handle') || '';
  var isView = handle === 'the-view' || handle === 'trieye-view' || handle === 'view';
  if (!isView) return;

  var img = wrap.querySelector('img');
  if (!img) return;

  function applyFromState() {
    var mirror = window.state && window.state.mirror ? String(window.state.mirror) : '';
    var file;
    var flip = false;
    if (mirror.toLowerCase() === 'dual') {
      file = 'wireframe-view-dual.png';
    } else if (mirror.toLowerCase() === 'right') {
      file = 'wireframe-view-single.png';
      flip = true;
    } else {
      file = 'wireframe-view-single.png';
    }
    if (img.src.indexOf(file) === -1) {
      img.src = img.src.replace(/wireframe-view-(single|dual)\.png/, file);
    }
    img.style.transform = flip ? 'scaleX(-1)' : '';
  }

  applyFromState();

  var formRoot = document.querySelector('.product-single') || document;
  formRoot.addEventListener('click', function() {
    requestAnimationFrame(applyFromState);
  });
};

document.addEventListener('DOMContentLoaded', function() { window.initSpecWireframeSwap(); });

// ============================================================
// Spec accordion — single-open behavior (opening one closes siblings)
// ============================================================
window.initSpecAccordion = function initSpecAccordion(rootEl) {
  var root = rootEl || document;
  var panels = root.querySelectorAll('.spec-panel, .spec-size-modal');
  panels.forEach(function(panel) {
    var accs = panel.querySelectorAll('.spec-acc');
    accs.forEach(function(acc) {
      if (acc._specAccBound) return;
      acc._specAccBound = true;
      acc.addEventListener('toggle', function() {
        if (acc.open) {
          accs.forEach(function(other) {
            if (other !== acc && other.open) other.open = false;
          });
        }
      });
    });
  });
};

document.addEventListener('DOMContentLoaded', function() { window.initSpecAccordion(); });

// ============================================================
// Variant thumbnail prefetch — eliminates the ~2s blank-thumbnail
// flicker on lens-type / color swap.
//
// filterGalleryByColor() rebuilds the gallery via gallery.innerHTML,
// inserting fresh <img> tags for the selected variant. Those URLs
// live only as HTML strings in _galleryCache (they're not painted
// in the DOM until the user clicks a variant), so the browser
// has never fetched them and shows a blank placeholder while the
// network round-trip completes.
//
// Fix: after first paint, on idle, inject <link rel="prefetch"
// as="image"> for every unique thumbnail URL we'll potentially
// need. The browser fetches them at low priority and stores them
// in HTTP cache, so the eventual <img src="…"> hits cache and
// paints instantly with no flicker.
//
// Cap: 200 unique URLs to cover even The View's 130 cached thumbs
// (50 variants × ~3 views, after dedup of shared shots). Each thumb
// is ~10KB at 114×144, so worst case ~1.3MB at idle on a session.
// Acceptable for desktop; mobile users on slow connections short-
// circuit early via the visibility/connection check below.
// ============================================================
window.addEventListener('load', function() {
  function _prefetchVariantThumbs() {
    if (typeof _cacheGallery === 'function') _cacheGallery();
    if (typeof _galleryCache === 'undefined' || !_galleryCache || !_galleryCache.thumbs.length) return;
    // Slow-connection escape hatch: skip prefetch on 2g/slow-2g or save-data mode.
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (c && (c.saveData || /^(slow-)?2g$/.test(c.effectiveType || ''))) return;
    var seen = {};
    var added = 0;
    for (var i = 0; i < _galleryCache.thumbs.length && added < 200; i++) {
      var tmp = document.createElement('div');
      tmp.innerHTML = _galleryCache.thumbs[i].html;
      var img = tmp.querySelector('img');
      if (!img) continue;
      var src = img.getAttribute('src');
      if (!src || seen[src]) continue;
      seen[src] = true;
      var link = document.createElement('link');
      link.rel = 'prefetch';
      link.as = 'image';
      link.href = src;
      document.head.appendChild(link);
      added++;
    }
  }
  if ('requestIdleCallback' in window) {
    requestIdleCallback(_prefetchVariantThumbs, { timeout: 4000 });
  } else {
    setTimeout(_prefetchVariantThumbs, 2500);
  }
});



/* 2026-09-26: the M / S legend pills in the configurator title look like the size pills
   on the buttons, and people tap them (Clarity dead clicks). Make a tap select that size. */
(function () {
  document.addEventListener('click', function (ev) {
    var pill = ev.target && ev.target.closest && ev.target.closest('.px-title-legend__pill');
    if (!pill) return;
    var want = (pill.textContent || '').trim().toUpperCase();
    var btn = [].slice.call(document.querySelectorAll('.px-btn')).filter(function (b) {
      var sp = b.querySelector('.px-btn__size-pill');
      return sp && (sp.textContent || '').trim().toUpperCase() === want && b.offsetParent !== null && !b.disabled;
    })[0];
    if (btn) { btn.click(); try { btn.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch (e) {} }
  }, true);
})();
